/**
 * PEPlan / Planungsrechner – Strategischer Planungsrechner (Sandbox-Simulation).
 * STRIKT: Alle Interaktionen laufen über `usePlanningSandboxStore`.
 * Keinerlei Schreibzugriffe auf `useAppStore`, `clientData` oder Firestore!
 *
 * Layout in Anlehnung an Planungsanalyse (`PEProfitControlPanel` + `PEProfitReport`):
 * Links gegliedertes Steuerungs-Panel (Tabs), rechts große Vorher/Nachher-Auswertung,
 * darunter Mitarbeiter-Spiegel und aufklappbarer Dienstleistungs-Kalkulator.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  RotateCcw,
  Trash2,
  TrendingUp,
  UserMinus,
  UserPlus,
  Users,
  Wallet,
  Calculator,
  Briefcase,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  ArrowLeft,
  FileText,
  Pencil,
  Save,
} from 'lucide-react';
import useAppStore from '../store/appStore';
import useAdminStore from '../store/adminStore';
import { useCalculationsSelector } from '../store/selectors/calculations';
import { evaluateRentabilitaet } from '../store/utils';
import { auth } from '../config/firebase';
import { buildPlanningBaselineFromLive } from '../utils/planningBaselineSnapshot';
import usePlanningSandboxStore, {
  type PlanningSavedScenarioEntry,
} from '../store/usePlanningSandboxStore';
import {
  calculatePlanningServiceKalkulation,
  type PlanningMetricComparison,
} from '../utils/planningEngine';
import {
  getBwaJahreswert,
  resolveBwaBetriebskostenOptions,
} from '../components/peProfit/peProfitBwaKeys';
import { currencyLabel, formatMoney } from '../utils/currencyFormatter';

export interface PePlanPageProps {
  /** 'god' = interner Planungsrechner (ohne Premium-Badge) · 'premium' = PEPlan für Mandanten */
  mode?: 'god' | 'premium';
}

type ControlSectionTab = 'personal' | 'inhaber' | 'kosten' | 'preis';

// ============================================================================
// FORMATTER
// ============================================================================

/** Unfokussierte Anzeige: Nachkommastellen nicht wegrunden, wenn vorhanden */
const formatUnfocusedNumber = (v: number, decimals = 0): string => {
  const safe = Number.isFinite(v) ? v : 0;
  if (decimals > 0) return fmtNum(safe, decimals);
  const hasFraction = Math.abs(safe - Math.round(safe)) >= 0.005;
  return hasFraction ? fmtNum(safe, 2) : String(Math.round(safe));
};

const fmtNum = (val: number, decimals = 2): string =>
  (Number.isFinite(val) ? val : 0).toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const fmtPct = (val: number, decimals = 1): string => `${fmtNum(val, decimals)} %`;

const makeFormatCurrency =
  (currencyCode: string) =>
  (value: number, decimals = 0): string =>
    formatMoney(Number.isFinite(value) ? value : 0, currencyCode, decimals);

/** Standard-PE-Tooltip: beige Hintergrund (#E6C299), schwarze Schrift — exakt wie im restlichen System */
const pePlanTooltipStyles = `
  [data-pe-tooltip] { position: relative; }
  @media (hover: hover) {
    [data-pe-tooltip]:hover::after {
      content: attr(data-pe-tooltip);
      position: absolute;
      bottom: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%);
      background-color: #E6C299;
      color: #000000;
      border: 1px solid #ffffff;
      padding: 8px 10px;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 800;
      line-height: 1.35;
      white-space: normal;
      width: max-content;
      max-width: min(17rem, 75vw);
      text-align: left;
      z-index: 9999;
      box-shadow: 0 4px 15px rgba(0,0,0,0.8);
      pointer-events: none;
      opacity: 0;
      animation: pePlanTipIn 0.2s forwards;
    }
    [data-pe-tooltip]:hover::before {
      content: '';
      position: absolute;
      bottom: 100%;
      left: 50%;
      transform: translateX(-50%);
      border-width: 6px;
      border-style: solid;
      border-color: #E6C299 transparent transparent transparent;
      z-index: 9999;
      pointer-events: none;
      opacity: 0;
      animation: pePlanTipIn 0.2s forwards;
    }
    [data-pe-tooltip][data-pe-tip-below]:hover::after {
      bottom: auto;
      top: calc(100% + 8px);
      animation: pePlanTipInBelow 0.2s forwards;
    }
    [data-pe-tooltip][data-pe-tip-below]:hover::before {
      bottom: auto;
      top: 100%;
      border-color: transparent transparent #E6C299 transparent;
      animation: pePlanTipInBelow 0.2s forwards;
    }
  }
  @keyframes pePlanTipIn {
    to { opacity: 1; transform: translateX(-50%) translateY(-4px); }
  }
  @keyframes pePlanTipInBelow {
    to { opacity: 1; transform: translateX(-50%) translateY(4px); }
  }
  .peplan-step-wrap {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    width: 100%;
    min-width: 0;
    min-height: 2.25rem;
    box-sizing: border-box;
  }
  .peplan-step-input {
    flex: 1 1 0%;
    min-width: 0 !important;
    width: 100% !important;
    box-sizing: border-box;
    background: linear-gradient(145deg, #000000, #0a0a0a);
    color: #ffffff;
    border: 1px solid #334155;
    border-radius: 0.5rem;
    padding: 0.35rem 0.25rem;
    text-align: center;
    font-family: 'JetBrains Mono', monospace;
    font-weight: 700;
    font-size: 0.85rem;
    outline: none;
  }
  .peplan-step-input:focus {
    border-color: #dcb68a;
  }
  .peplan-step-btn {
    flex: 0 0 1.85rem;
    width: 1.85rem;
    min-width: 1.85rem;
    height: 2.25rem;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(145deg, #111, #000);
    color: #dcb68a;
    border: 1px solid #334155;
    border-radius: 0.5rem;
    font-size: 1rem;
    font-weight: 800;
    cursor: pointer;
    user-select: none;
  }
  .peplan-step-btn:hover {
    background: #dcb68a;
    color: #000;
    border-color: #dcb68a;
  }

  /* ==========================================================================
     HOCHKONTRAST LIGHT-MODE FÜR PEPLAN / PLANUNGSRECHNER (html:not(.dark))
     Garantiert tiefdunkle Schrift auf warm-hellem PE-One-Hintergrund —
     keine blasse Schrift auf hellem Grund!
     ========================================================================== */
  html:not(.dark) .peplan-shell {
    background-color: var(--pe-bg-primary, #D8D3CC) !important;
    color: #1A1614 !important;
  }
  html:not(.dark) .peplan-shell .bg-slate-900,
  html:not(.dark) .peplan-shell .bg-slate-900\\/95,
  html:not(.dark) .peplan-shell .bg-slate-900\\/90,
  html:not(.dark) .peplan-shell .bg-slate-900\\/80,
  html:not(.dark) .peplan-shell .bg-slate-900\\/70,
  html:not(.dark) .peplan-shell .bg-slate-900\\/50 {
    background-color: #EEEBE6 !important;
    border-color: #BDB6AE !important;
  }
  html:not(.dark) .peplan-shell .bg-slate-950,
  html:not(.dark) .peplan-shell .bg-slate-950\\/90,
  html:not(.dark) .peplan-shell .bg-slate-950\\/85,
  html:not(.dark) .peplan-shell .bg-slate-950\\/80,
  html:not(.dark) .peplan-shell .bg-slate-950\\/70,
  html:not(.dark) .peplan-shell .bg-slate-950\\/60,
  html:not(.dark) .peplan-shell .bg-slate-950\\/50,
  html:not(.dark) .peplan-shell .bg-slate-950\\/40 {
    background-color: #E2DDD6 !important;
    border-color: #BDB6AE !important;
  }
  html:not(.dark) .peplan-shell .border-slate-800,
  html:not(.dark) .peplan-shell .border-slate-800\\/80,
  html:not(.dark) .peplan-shell .border-slate-700,
  html:not(.dark) .peplan-shell .border-slate-600 {
    border-color: #B5AEA5 !important;
  }
  /* Primäre Texte: Tiefschwarz / Dunkelbraun für maximalen Kontrast */
  html:not(.dark) .peplan-shell .text-white,
  html:not(.dark) .peplan-shell .text-slate-100,
  html:not(.dark) .peplan-shell .text-slate-200 {
    color: #1A1614 !important;
  }
  /* Sekundäre Texte: Sattes Anthrazit statt verwaschenem Grau */
  html:not(.dark) .peplan-shell .text-slate-300,
  html:not(.dark) .peplan-shell .text-slate-400,
  html:not(.dark) .peplan-shell .text-slate-500 {
    color: #3F3A36 !important;
  }
  /* Gold/Beige-Akzent im Light-Mode: Dunkles Bronze (#6B471E) für glasklare Lesbarkeit auf hellem Grund */
  html:not(.dark) .peplan-shell .text-\\[\\#dcb68a\\],
  html:not(.dark) .peplan-shell .text-\\[\\#dcb68a\\]\\/85 {
    color: #6B471E !important;
  }
  /* Ausgewählte Reiter mit bg-[#dcb68a] behalten schwarze Schrift */
  html:not(.dark) .peplan-shell .bg-\\[\\#dcb68a\\] {
    background-color: #C89D66 !important;
    color: #1A1614 !important;
    border-color: #8D6435 !important;
  }
  html:not(.dark) .peplan-shell .bg-\\[\\#dcb68a\\]\\/15,
  html:not(.dark) .peplan-shell .bg-\\[\\#dcb68a\\]\\/10,
  html:not(.dark) .peplan-shell .bg-\\[\\#dcb68a\\]\\/5 {
    background-color: rgba(141, 100, 53, 0.14) !important;
    border-color: rgba(141, 100, 53, 0.45) !important;
  }
  /* Eingabefelder, Selects & Stepper im Light-Mode */
  html:not(.dark) .peplan-shell input[type="text"],
  html:not(.dark) .peplan-shell input[type="number"],
  html:not(.dark) .peplan-shell select,
  html:not(.dark) .peplan-shell .peplan-step-input {
    background: #FFFFFF !important;
    color: #1A1614 !important;
    border: 1px solid #9E968D !important;
    box-shadow: inset 0 1px 2px rgba(0,0,0,0.06) !important;
  }
  html:not(.dark) .peplan-shell .peplan-step-btn {
    background: #E2DDD6 !important;
    color: #5C3B14 !important;
    border: 1px solid #9E968D !important;
  }
  html:not(.dark) .peplan-shell .peplan-step-btn:hover {
    background: #C89D66 !important;
    color: #1A1614 !important;
  }
  /* Status- & Ampelfarben im Light-Mode: Dunkle, satte Töne statt heller Neonfarben */
  html:not(.dark) .peplan-shell .text-emerald-400,
  html:not(.dark) .peplan-shell .text-emerald-300 {
    color: #146C32 !important;
  }
  html:not(.dark) .peplan-shell .text-rose-400,
  html:not(.dark) .peplan-shell .text-rose-300,
  html:not(.dark) .peplan-shell .text-red-400 {
    color: #B91C1C !important;
  }
  html:not(.dark) .peplan-shell .text-amber-400,
  html:not(.dark) .peplan-shell .text-amber-300 {
    color: #9A4A06 !important;
  }
  html:not(.dark) .peplan-shell .text-lime-400,
  html:not(.dark) .peplan-shell .text-lime-300 {
    color: #3F6212 !important;
  }
  html:not(.dark) .peplan-shell .text-sky-300,
  html:not(.dark) .peplan-shell .text-blue-400 {
    color: #0369A1 !important;
  }
  html:not(.dark) .peplan-shell .text-purple-400,
  html:not(.dark) .peplan-shell .text-purple-300 {
    color: #6B21A8 !important;
  }
  html:not(.dark) .peplan-shell .bg-emerald-950\\/25,
  html:not(.dark) .peplan-shell .bg-emerald-950\\/20,
  html:not(.dark) .peplan-shell .bg-emerald-950\\/15 {
    background-color: #DCFCE7 !important;
    border-color: #86EFAC !important;
  }
  html:not(.dark) .peplan-shell .bg-rose-950\\/30,
  html:not(.dark) .peplan-shell .bg-rose-950\\/25,
  html:not(.dark) .peplan-shell .bg-rose-950\\/20,
  html:not(.dark) .peplan-shell .bg-red-950\\/25 {
    background-color: #FEE2E2 !important;
    border-color: #FCA5A5 !important;
  }
  html:not(.dark) .peplan-shell .bg-amber-950\\/25,
  html:not(.dark) .peplan-shell .bg-amber-950\\/15 {
    background-color: #FEF3C7 !important;
    border-color: #FCD34D !important;
  }
  html:not(.dark) .peplan-shell .bg-lime-950\\/25 {
    background-color: #ECFCCB !important;
    border-color: #BEF264 !important;
  }
  html:not(.dark) .peplan-shell .bg-purple-950\\/25 {
    background-color: #F3E8FF !important;
    border-color: #D8B4FE !important;
  }
  html:not(.dark) .peplan-shell .carbon-weave-bg {
    background: #E2DDD6 !important;
    border-color: #B5AEA5 !important;
  }
`;

// ============================================================================
// ZAHLENFELD MIT VOLLEM LABEL, +/- STEPPER, SLIDER & TOOLTIP
// ============================================================================

const NobleNumberControl: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  showSlider?: boolean;
  decimals?: number;
  hint?: string;
  tooltip?: string;
}> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 100000,
  step = 1,
  suffix,
  showSlider = true,
  decimals = 0,
  hint,
  tooltip,
}) => {
  const [draft, setDraft] = useState<string>(() => fmtNum(value, decimals));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDraft(formatUnfocusedNumber(value, decimals));
    }
  }, [value, focused, decimals]);

  const clampVal = (v: number) => Math.min(max, Math.max(min, v));

  const commit = (raw: string) => {
    const cleaned = String(raw ?? '')
      .replace(/\./g, '')
      .replace(',', '.')
      .replace(/[^0-9.-]/g, '');
    const parsed = parseFloat(cleaned);
    const next = clampVal(Number.isFinite(parsed) ? parsed : 0);
    onChange(next);
  };

  const nudge = (dir: -1 | 1) => {
    const base = Number.isFinite(value) ? value : 0;
    let rounded: number;
    if (step >= 1) {
      const rem = ((base % step) + step) % step;
      const isAligned = rem < 0.01 || step - rem < 0.01;
      if (!isAligned) {
        rounded = dir > 0 ? Math.ceil(base / step) * step : Math.floor(base / step) * step;
      } else {
        rounded = Math.round((base + dir * step) / step) * step;
      }
    } else {
      rounded = Math.round((base + dir * step) * 100) / 100;
    }
    onChange(clampVal(rounded));
  };

  return (
    <div
      className="space-y-2 min-w-0"
      data-pe-tooltip={tooltip || undefined}
    >
      <div className="space-y-0.5">
        <label className="block text-xs sm:text-sm font-black uppercase tracking-wide text-slate-100 leading-snug break-words">
          {label}
        </label>
        {hint && (
          <span className="block text-xs text-slate-400 leading-snug break-words">
            {hint}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 pt-0.5">
        <div className="flex items-center gap-1.5 flex-1">
          <button
            type="button"
            onClick={() => nudge(-1)}
            className="h-9 w-9 shrink-0 rounded-lg border border-slate-700 bg-slate-950/90 text-[#dcb68a] font-bold text-base flex items-center justify-center hover:border-[#dcb68a]/60 transition-colors"
            aria-label={`${label} verringern`}
          >
            −
          </button>
          <input
            type="text"
            size={1}
            inputMode="decimal"
            value={focused ? draft : formatUnfocusedNumber(value, decimals)}
            onFocus={(e) => {
              setFocused(true);
              setDraft(String(value ?? 0).replace('.', ','));
              const el = e.currentTarget;
              requestAnimationFrame(() => el.select());
            }}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              setFocused(false);
              commit(draft);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            className="peplan-step-input"
          />
          <button
            type="button"
            onClick={() => nudge(1)}
            className="h-9 w-9 shrink-0 rounded-lg border border-slate-700 bg-slate-950/90 text-[#dcb68a] font-bold text-base flex items-center justify-center hover:border-[#dcb68a]/60 transition-colors"
            aria-label={`${label} erhöhen`}
          >
            +
          </button>
          {suffix && (
            <span className="text-xs font-bold text-slate-300 min-w-[2.2rem] text-left pl-1">
              {suffix}
            </span>
          )}
        </div>
      </div>

      {showSlider && (
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={clampVal(Number.isFinite(value) ? value : 0)}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-full h-2 accent-[#dcb68a] cursor-pointer"
        />
      )}
    </div>
  );
};

// ============================================================================
// KOMPAKTES KALKULATOR-FELD (OHNE ÜBERLAPPUNG — WIE IM ORIGINAL-KALKULATOR)
// ============================================================================

const CompactCalcField: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  decimals?: number;
  unit?: string;
  accent?: boolean;
}> = ({
  label,
  value,
  onChange,
  min = 0,
  max = 10000,
  step = 5,
  decimals = 2,
  unit = '',
  accent = false,
}) => {
  const [draft, setDraft] = useState<string>(() => fmtNum(value, decimals));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDraft(formatUnfocusedNumber(value, decimals));
    }
  }, [value, focused, decimals]);

  const clampVal = (v: number) => Math.min(max, Math.max(min, v));

  const commit = (raw: string) => {
    const cleaned = String(raw ?? '')
      .replace(/\./g, '')
      .replace(',', '.')
      .replace(/[^0-9.-]/g, '');
    const parsed = parseFloat(cleaned);
    onChange(clampVal(Number.isFinite(parsed) ? parsed : 0));
  };

  const nudge = (dir: -1 | 1) => {
    const base = Number.isFinite(value) ? value : 0;
    let rounded: number;
    if (step >= 1) {
      const rem = ((base % step) + step) % step;
      const isAligned = rem < 0.01 || step - rem < 0.01;
      if (!isAligned) {
        rounded = dir > 0 ? Math.ceil(base / step) * step : Math.floor(base / step) * step;
      } else {
        rounded = Math.round((base + dir * step) / step) * step;
      }
    } else {
      rounded = Math.round((base + dir * step) * 100) / 100;
    }
    onChange(clampVal(rounded));
  };

  const displayStr = focused
    ? draft
    : `${formatUnfocusedNumber(value, decimals)}${unit ? ` ${unit}` : ''}`;

  return (
    <div className="flex flex-col min-w-0 w-full">
      <label
        className={`text-[11px] font-black uppercase tracking-wider text-center mb-1.5 truncate ${
          accent ? 'text-[#dcb68a]' : 'text-slate-300'
        }`}
      >
        {label}
      </label>
      <div className="peplan-step-wrap">
        <button
          type="button"
          onClick={() => nudge(-1)}
          className="peplan-step-btn"
          aria-label={`${label} verringern`}
        >
          −
        </button>
        <input
          type="text"
          size={1}
          inputMode="decimal"
          value={displayStr}
          onFocus={(e) => {
            setFocused(true);
            setDraft(String(value ?? 0).replace('.', ','));
            const el = e.currentTarget;
            requestAnimationFrame(() => el.select());
          }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            setFocused(false);
            commit(draft);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          className="peplan-step-input"
        />
        <button
          type="button"
          onClick={() => nudge(1)}
          className="peplan-step-btn"
          aria-label={`${label} erhöhen`}
        >
          +
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// VORHER / NACHHER ZEILE (ANLEHNUNG AN PEPROFIT-REPORT)
// ============================================================================

const ReportCompareRow: React.FC<{
  label: string;
  subtitle?: string;
  comp: PlanningMetricComparison;
  formatVal: (v: number) => string;
  quoteComp?: PlanningMetricComparison;
  invertGood?: boolean;
  isResult?: boolean;
}> = ({ label, subtitle, comp, formatVal, quoteComp, invertGood = false, isResult = false }) => {
  const delta = comp.deltaAbsolut;
  const hasDelta = Math.abs(delta) >= 0.01;
  const isPositive = delta > 0;
  const isGood = invertGood ? !isPositive : isPositive;

  const quoteDelta = quoteComp ? quoteComp.deltaAbsolut : 0;
  const hasQuoteDelta = quoteComp && Math.abs(quoteDelta) >= 0.05;

  return (
    <tr
      className={
        isResult
          ? 'border-t-2 border-slate-700 bg-slate-900/90 font-bold'
          : 'border-b border-slate-800/80 hover:bg-slate-900/40'
      }
    >
      <td className="px-4 py-3.5 text-left">
        <span className="block text-sm text-white font-semibold">{label}</span>
        {subtitle && <span className="block text-xs text-slate-400 mt-0.5">{subtitle}</span>}
      </td>
      <td className="px-4 py-3.5 text-right font-mono text-sm text-slate-300 tabular-nums">
        <span className="block">{formatVal(comp.vorher)}</span>
        {quoteComp && (
          <span className="block text-xs font-semibold text-slate-400 mt-0.5">
            ({fmtPct(quoteComp.vorher, 1)})
          </span>
        )}
      </td>
      <td className="px-4 py-3.5 text-right font-mono text-sm md:text-base font-black text-[#dcb68a] tabular-nums">
        <span className="block">{formatVal(comp.nachher)}</span>
        {quoteComp && (
          <span className="block text-xs font-bold text-[#dcb68a]/85 mt-0.5">
            ({fmtPct(quoteComp.nachher, 1)})
          </span>
        )}
      </td>
      <td className="px-4 py-3.5 text-right font-mono text-sm tabular-nums">
        {hasDelta || hasQuoteDelta ? (
          <div className="inline-flex flex-col items-end gap-0.5">
            {hasDelta ? (
              <span
                className={`inline-block px-2 py-0.5 rounded font-bold ${
                  isGood ? 'text-emerald-400 bg-emerald-500/10' : 'text-rose-400 bg-rose-500/10'
                }`}
              >
                {delta > 0 ? '+' : ''}
                {formatVal(delta)}
              </span>
            ) : (
              <span className="text-slate-400">±0</span>
            )}
            {hasQuoteDelta && (
              <span
                className={`text-xs font-bold ${
                  (invertGood ? quoteDelta < 0 : quoteDelta > 0)
                    ? 'text-emerald-400'
                    : 'text-rose-400'
                }`}
              >
                ({quoteDelta > 0 ? '+' : ''}
                {fmtPct(quoteDelta, 1)})
              </span>
            )}
          </div>
        ) : (
          <span className="text-slate-500">±0</span>
        )}
      </td>
    </tr>
  );
};

// ============================================================================
// GEWINN-GAUGE FÜR DEN EINGEBETTETEN KALKULATOR (1:1 BLAUPAUSE SOLO-SIMULATOR)
// ============================================================================

const PePlanGewinnGauge: React.FC<{
  gewinn: number;
  gewinnPercent: number;
  targetPercent: number;
  formatCurrency: (v: number, d?: number) => string;
}> = ({ gewinn, gewinnPercent, targetPercent = 10, formatCurrency }) => {
  const gaugeSettings = useMemo(() => {
    if (gewinnPercent >= targetPercent * 1.6)
      return {
        color: '#8b5cf6',
        textColor: 'text-purple-400',
        text: 'Hervorragendes Ergebnis',
        highlightColor: '#a78bfa',
      };
    if (gewinnPercent >= targetPercent * 1.2)
      return {
        color: '#3b82f6',
        textColor: 'text-blue-400',
        text: 'Gesunde Rücklagen',
        highlightColor: '#60a5fa',
      };
    if (gewinnPercent >= targetPercent)
      return {
        color: '#16a34a',
        textColor: 'text-emerald-400',
        text: 'Solider Gewinn',
        highlightColor: '#4ade80',
      };
    if (gewinnPercent >= 0)
      return {
        color: '#ca8a04',
        textColor: 'text-amber-400',
        text: 'Kostendeckend',
        highlightColor: '#facc15',
      };
    return {
      color: '#dc2626',
      textColor: 'text-rose-400',
      text: 'Verlust',
      highlightColor: '#f87171',
    };
  }, [gewinnPercent, targetPercent]);

  const radius = 38;
  const circumference = 2 * Math.PI * radius;
  const maxScale = Math.max(20, targetPercent * 1.6);
  const visualPercent = Math.min(Math.max(0, gewinnPercent), maxScale) / maxScale;
  const offset = circumference * (1 - visualPercent);

  return (
    <div
      className="mt-4 p-4 rounded-xl bg-slate-950/90 flex flex-wrap items-center justify-around gap-4"
      style={{
        borderWidth: '1px',
        borderColor: gaugeSettings.highlightColor,
        boxShadow: `0 0 20px ${gaugeSettings.highlightColor}25`,
      }}
    >
      <div className="relative w-20 h-20 flex-shrink-0">
        <svg className="w-full h-full overflow-visible" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={radius} fill="none" stroke="#1e293b" strokeWidth="8" />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={gaugeSettings.color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform="rotate(-90 50 50)"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-[11px] font-mono font-bold text-white leading-none">
          {fmtNum(gewinnPercent, 1)}%
        </div>
      </div>

      <div className="text-center">
        <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
          Gewinn / Bedienzeit
        </div>
        <div className={`text-2xl font-black font-mono ${gaugeSettings.textColor}`}>
          {formatCurrency(gewinn, 2)}
        </div>
      </div>

      <div className="text-center">
        <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
          Rücklage-Quote
        </div>
        <div className={`text-2xl font-black font-mono ${gaugeSettings.textColor}`}>
          {fmtPct(gewinnPercent, 1)}
        </div>
      </div>

      <div className="text-center">
        <div className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
          Bewertung (Ziel ≥ {fmtNum(targetPercent, 0)} %)
        </div>
        <div className={`text-base font-black uppercase tracking-wider ${gaugeSettings.textColor}`}>
          {gaugeSettings.text}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// HAUPTKOMPONENTE: PePlanPage
// ============================================================================

const PePlanPage: React.FC<PePlanPageProps> = ({ mode = 'premium' }) => {
  const clientData = useAppStore((s) => s.clientData);
  const setClientData = useAppStore((s) => s.setClientData);
  const openPremiumModal = useAppStore((s) => s.openPremiumModal);
  const settings = useAppStore((s) => s.settings);
  const liveCalc = useCalculationsSelector();
  const impersonatedClientId = useAdminStore((s) => s.impersonatedClientId);
  /** PEPlan (Premium) und Planungsrechner (God) teilen Engine, aber getrennte Archive */
  const toolChannel = mode === 'god' ? 'planungsrechner' : 'pe-plan';
  const archivKey =
    mode === 'god' ? 'planungsrechnerArchiv' : 'pePlanArchiv';

  const [viewMode, setViewMode] = useState<'archive' | 'workspace'>('archive');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTitel, setNewTitel] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameTitel, setRenameTitel] = useState('');
  const [saveToast, setSaveToast] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<ControlSectionTab>('personal');
  const [openBestandGroups, setOpenBestandGroups] = useState<{
    produktiv: boolean;
    azubi: boolean;
    unproduktiv: boolean;
  }>({
    produktiv: false,
    azubi: false,
    unproduktiv: false,
  });
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<string | null>(null);
  const [mitarbeiterSpiegelOpen, setMitarbeiterSpiegelOpen] = useState(false);
  const [kalkulatorOpen, setKalkulatorOpen] = useState(false);

  // Lokale Form-States für Tab "Kosten, Invest & Dienstleistungen" (analog PEProfitControlPanel)
  const [gkKey, setGkKey] = useState<string>('');
  const [gkNeuName, setGkNeuName] = useState<string>('');
  const [gkZielwertMonat, setGkZielwertMonat] = useState<number>(0);
  const [gkNotiz, setGkNotiz] = useState<string>('');

  const [invNameDraft, setInvNameDraft] = useState<string>('');
  const [invRateDraft, setInvRateDraft] = useState<number>(250);
  const [invInputModus, setInvInputModus] = useState<'monat' | 'gesamt'>('monat');

  const [dlNameDraft, setDlNameDraft] = useState<string>('');
  const [dlUmsatzDraft, setDlUmsatzDraft] = useState<number>(500);
  const [dlMaterialDraft, setDlMaterialDraft] = useState<number>(50);

  const isHydrated = usePlanningSandboxStore((s) => s.isHydrated);
  const baseline = usePlanningSandboxStore((s) => s.baseline);
  const globalSettings = usePlanningSandboxStore((s) => s.globalSettings);
  const activeSzenarioId = usePlanningSandboxStore((s) => s.activeSzenarioId);
  const inputs = usePlanningSandboxStore((s) => s.inputs);
  const outputs = usePlanningSandboxStore((s) => s.outputs);
  const kalkulator = usePlanningSandboxStore((s) => s.kalkulator);

  const hydrateFromLiveAkte = usePlanningSandboxStore((s) => s.hydrateFromLiveAkte);
  const resetScenarioToBaseline = usePlanningSandboxStore((s) => s.resetScenarioToBaseline);
  const loadSavedScenarioIntoSandbox = usePlanningSandboxStore(
    (s) => s.loadSavedScenarioIntoSandbox
  );
  const startNewScenarioInSandbox = usePlanningSandboxStore((s) => s.startNewScenarioInSandbox);
  const buildCurrentScenarioEntry = usePlanningSandboxStore((s) => s.buildCurrentScenarioEntry);
  const setSzenarioTitel = usePlanningSandboxStore((s) => s.setSzenarioTitel);
  const addNewHire = usePlanningSandboxStore((s) => s.addNewHire);
  const updateNewHire = usePlanningSandboxStore((s) => s.updateNewHire);
  const removeNewHire = usePlanningSandboxStore((s) => s.removeNewHire);
  const setDeparture = usePlanningSandboxStore((s) => s.setDeparture);
  const updateOwnerInput = usePlanningSandboxStore((s) => s.updateOwnerInput);
  const setZusatzMaterialMonatNetto = usePlanningSandboxStore((s) => s.setZusatzMaterialMonatNetto);
  const setMaterialNotiz = usePlanningSandboxStore((s) => s.setMaterialNotiz);
  const upsertBwaAdjustment = usePlanningSandboxStore((s) => s.upsertBwaAdjustment);
  const removeBwaAdjustment = usePlanningSandboxStore((s) => s.removeBwaAdjustment);
  const addInvestmentItem = usePlanningSandboxStore((s) => s.addInvestmentItem);
  const updateInvestmentItem = usePlanningSandboxStore((s) => s.updateInvestmentItem);
  const removeInvestmentItem = usePlanningSandboxStore((s) => s.removeInvestmentItem);
  const addNewServiceItem = usePlanningSandboxStore((s) => s.addNewServiceItem);
  const updateNewServiceItem = usePlanningSandboxStore((s) => s.updateNewServiceItem);
  const removeNewServiceItem = usePlanningSandboxStore((s) => s.removeNewServiceItem);
  const updatePriceAdjustment = usePlanningSandboxStore((s) => s.updatePriceAdjustment);
  const updateKalkulatorInputs = usePlanningSandboxStore((s) => s.updateKalkulatorInputs);
  const toggleKalkulatorMaterial = usePlanningSandboxStore((s) => s.toggleKalkulatorMaterial);
  const toggleKalkulatorDevice = usePlanningSandboxStore((s) => s.toggleKalkulatorDevice);
  const resetKalkulatorNeuerPreisToAuto = usePlanningSandboxStore(
    (s) => s.resetKalkulatorNeuerPreisToAuto
  );

  // Tool gewechselt (PEPlan ↔ Planungsrechner): Archiv-Ansicht + lokaler Workspace-State zurück
  useEffect(() => {
    setViewMode('archive');
    setShowCreateForm(false);
    setExpandedEmployeeId(null);
    setRenamingId(null);
  }, [toolChannel]);

  useEffect(() => {
    if (!liveCalc?.ready) return;
    const mandantUid =
      impersonatedClientId ||
      (() => {
        try {
          return sessionStorage.getItem('GOD_MODE_ID');
        } catch {
          return null;
        }
      })() ||
      auth.currentUser?.uid ||
      'unknown';
    const year =
      (typeof localStorage !== 'undefined' && localStorage.getItem('CURRENT_YEAR')) ||
      String(new Date().getFullYear());

    const snapshot = buildPlanningBaselineFromLive(clientData, liveCalc, settings, {
      mandantUid,
      year,
    });
    hydrateFromLiveAkte({ ...snapshot, toolChannel });
  }, [
    liveCalc,
    clientData,
    clientData?.Land,
    clientData?.land,
    settings,
    settings?.selectedCountryId,
    impersonatedClientId,
    hydrateFromLiveAkte,
    toolChannel,
  ]);

  const currencyCode = currencyLabel(
    liveCalc?.currencyCode || globalSettings?.currencyCode || settings?.country?.currency,
    clientData?.Land || clientData?.land || settings?.selectedCountryId
  );

  const formatCurrency = useMemo(() => makeFormatCurrency(currencyCode), [currencyCode]);

  const serviceKalkResult = useMemo(() => {
    if (!baseline || !outputs || !globalSettings) return null;
    return calculatePlanningServiceKalkulation({
      ...kalkulator,
      materialListe: baseline.materialListe,
      geraeteListe: baseline.geraeteListe,
      outputs,
      globalSettings,
      isEffectiveSolo: outputs.isEffectiveSolo,
    });
  }, [baseline, outputs, globalSettings, kalkulator]);

  if (!isHydrated || !baseline || !outputs || !globalSettings) {
    return (
      <div className="peplan-shell min-h-screen bg-slate-950 text-slate-100 p-6">
        <style>{pePlanTooltipStyles}</style>
        <div className="max-w-7xl mx-auto bg-slate-900/90 border border-slate-800 rounded-xl p-8 text-sm text-slate-400">
          Lade Mandanten-Baseline in die isolierte Planungs-Sandbox…
        </div>
      </div>
    );
  }

  const activeMatSet = new Set(kalkulator.activeMaterialIds);
  const activeDevSet = new Set(kalkulator.activeDeviceIds);
  const isGodView = mode === 'god';
  const activeDeparturesCount = inputs.abgaenge.filter((d) => d.aktiv).length;
  const activeHiresCount = inputs.zuwachs.filter((h) => h.aktiv).length;
  const bwaAnpassungenList = inputs.investitionen.bwaAnpassungen || [];
  const neueDienstleistungenList = inputs.investitionen.neueDienstleistungen || [];
  const activeInvCount =
    inputs.investitionen.einzelInvestitionen.filter((i) => i.aktiv).length +
    bwaAnpassungenList.filter((b) => b.aktiv).length +
    neueDienstleistungenList.filter((d) => d.aktiv).length;

  // BWA-Kostenarten aus der geöffneten Akte auflösen (wie in Planungsanalyse / PEProfitControlPanel)
  const bwaRawData = (clientData?.bwaData as Record<string, unknown>) || {};
  const zeitraumMonate = baseline.zeitraumMonate || 12;
  const bwaOptions = resolveBwaBetriebskostenOptions(
    bwaRawData,
    (v) => formatCurrency(v),
    'monat',
    zeitraumMonate
  );
  const resolveBwaIstMonat = (key: string): number => {
    const jahr = getBwaJahreswert(key, bwaRawData);
    return Math.round(jahr / (zeitraumMonate > 0 ? zeitraumMonate : 12));
  };

  // Getrennte Archive: PEPlan → pePlanArchiv · Planungsrechner (God) → planungsrechnerArchiv
  const rawArchiv: PlanningSavedScenarioEntry[] = Array.isArray((clientData as any)?.[archivKey])
    ? (clientData as any)[archivKey]
    : [];
  const sortedArchiv = [...rawArchiv].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

  const formatArchivDatum = (ts: number): string =>
    new Date(ts || Date.now()).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const persistArchivList = (nextArchiv: PlanningSavedScenarioEntry[]) => {
    setClientData((prev: any) => ({
      ...prev,
      [archivKey]: nextArchiv,
    }));
  };

  const handleSaveCurrentScenario = (silent = false) => {
    const built = buildCurrentScenarioEntry();
    if (!built) return;
    const exists = rawArchiv.some((item) => item.id === built.id);
    const nextArchiv = exists
      ? rawArchiv.map((item) => (item.id === built.id ? built : item))
      : [built, ...rawArchiv];
    persistArchivList(nextArchiv);
    if (!silent) {
      setSaveToast('Szenario im Archiv gespeichert');
      setTimeout(() => setSaveToast(null), 2500);
    }
  };

  const handleCreateNewScenario = () => {
    const titel = newTitel.trim() || 'Planungs-Simulation';
    const created = startNewScenarioInSandbox(titel);
    if (created) {
      persistArchivList([created, ...rawArchiv]);
    }
    setShowCreateForm(false);
    setNewTitel('');
    setViewMode('workspace');
  };

  const handleOpenScenarioFromArchiv = (entry: PlanningSavedScenarioEntry) => {
    loadSavedScenarioIntoSandbox(entry);
    setViewMode('workspace');
  };

  const handleDeleteScenarioFromArchiv = (id: string) => {
    const entry = rawArchiv.find((item) => item.id === id);
    const label = entry?.titel?.trim() || 'diese Planungs-Simulation';
    if (!window.confirm(`„${label}“ wirklich unwiderruflich aus dem Archiv löschen?`)) return;
    if (renamingId === id) {
      setRenamingId(null);
      setRenameTitel('');
    }
    const nextArchiv = rawArchiv.filter((item) => item.id !== id);
    persistArchivList(nextArchiv);
  };

  const handleRenameScenarioInArchiv = () => {
    if (!renamingId) return;
    const clean = renameTitel.trim() || 'Planungs-Simulation';
    const nextArchiv = rawArchiv.map((item) =>
      item.id === renamingId
        ? {
            ...item,
            titel: clean,
            inputs: { ...item.inputs, szenarioTitel: clean },
            timestamp: Date.now(),
          }
        : item
    );
    persistArchivList(nextArchiv);
    if (activeSzenarioId === renamingId) {
      setSzenarioTitel(clean);
    }
    setRenamingId(null);
    setRenameTitel('');
  };

  // ==========================================================================
  // ARCHIV-ANSICHT (ANALOG PE PROFIT ARCHIV)
  // ==========================================================================
  if (viewMode === 'archive') {
    return (
      <div className="peplan-shell min-h-screen bg-slate-950 text-slate-100 p-4 md:p-6 pb-32 font-sans">
        <style>{pePlanTooltipStyles}</style>
        <div className="mx-auto w-full max-w-3xl space-y-6">
          <div className="carbon-weave-bg rounded-2xl border border-slate-800 px-5 py-5 md:px-6 shadow-xl">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.2em] text-[#dcb68a]">
                  {isGodView ? 'Gott-Modus · Arbeitstool' : 'Premium Tool'}
                </p>
                <h1 className="text-3xl font-bold text-white md:text-4xl mt-1">
                  {isGodView ? (
                    <>Planungsrechner Archiv</>
                  ) : (
                    <>
                      <span className="italic text-[#dcb68a]">PE</span>
                      <span className="font-bold text-white">Plan</span> Archiv
                    </>
                  )}
                </h1>
                <p className="mt-2 text-sm text-slate-400">
                  Wähle eine gespeicherte Planungs-Simulation aus dem Archiv oder lege ein neues Szenario an.
                </p>
              </div>
              {!isGodView && (
                <button
                  type="button"
                  onClick={() => openPremiumModal('pe-plan')}
                  className="rounded-lg border border-[#dcb68a]/40 bg-[#dcb68a]/10 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-[#dcb68a] hover:bg-[#dcb68a]/20"
                >
                  Tarifübersicht
                </button>
              )}
            </div>
          </div>

          {!showCreateForm ? (
            <button
              type="button"
              onClick={() => {
                setShowCreateForm(true);
                setRenamingId(null);
              }}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-[#dcb68a]/50 bg-[#dcb68a]/5 px-4 py-4 text-sm font-bold uppercase tracking-wider text-[#dcb68a] hover:bg-[#dcb68a]/10 md:w-auto md:px-6"
            >
              <Plus size={16} />
              Neue Planungs-Simulation erstellen
            </button>
          ) : (
            <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 md:p-5">
              <h3 className="text-base font-bold text-white">Neue Planungs-Simulation</h3>
              <p className="mt-1 text-sm text-slate-400">
                Gib einen Titel für das neue Szenario an (z. B. „Neue Fachkraft + 8 % Preisanpassung“).
              </p>
              <label className="mt-4 block">
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Titel
                </span>
                <input
                  type="text"
                  value={newTitel}
                  onChange={(e) => setNewTitel(e.target.value)}
                  placeholder="z. B. Preiserhöhung & Teilzeitkraft Q3"
                  className="w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#dcb68a]"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateNewScenario();
                  }}
                />
              </label>
              <div className="mt-5 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setNewTitel('');
                  }}
                  className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-slate-800"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleCreateNewScenario}
                  className="rounded-lg bg-[#dcb68a] px-4 py-2 text-sm font-bold text-slate-900 hover:bg-[#cfa878]"
                >
                  Anlegen &amp; öffnen
                </button>
              </div>
            </div>
          )}

          <div>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">
                Szenario-Archiv ({clientData?.firmenname || 'Mandant'})
              </h2>
              {sortedArchiv.length > 0 && (
                <span className="text-xs text-slate-500">
                  {sortedArchiv.length} {sortedArchiv.length === 1 ? 'Eintrag' : 'Einträge'}
                </span>
              )}
            </div>

            {sortedArchiv.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/50 px-4 py-10 text-center">
                <p className="text-sm text-slate-400">
                  Noch keine Planungs-Simulationen im Archiv gespeichert. Lege oben die erste Planung an.
                </p>
              </div>
            ) : (
              <ul className="space-y-2.5">
                {sortedArchiv.map((entry) => (
                  <li
                    key={entry.id}
                    className="rounded-xl border border-slate-700 bg-slate-900 transition-colors hover:border-slate-600"
                  >
                    {renamingId === entry.id ? (
                      <div className="space-y-3 p-4">
                        <label className="block">
                          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">
                            Titel umbenennen
                          </span>
                          <input
                            type="text"
                            value={renameTitel}
                            onChange={(e) => setRenameTitel(e.target.value)}
                            className="w-full rounded-lg border border-slate-600 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-[#dcb68a]"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleRenameScenarioInArchiv();
                              if (e.key === 'Escape') {
                                setRenamingId(null);
                                setRenameTitel('');
                              }
                            }}
                          />
                        </label>
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setRenamingId(null);
                              setRenameTitel('');
                            }}
                            className="rounded-lg border border-slate-600 px-3 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800"
                          >
                            Abbrechen
                          </button>
                          <button
                            type="button"
                            onClick={handleRenameScenarioInArchiv}
                            className="rounded-lg bg-[#dcb68a] px-3 py-2 text-xs font-bold text-slate-900 hover:bg-[#cfa878]"
                          >
                            Speichern
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
                        <button
                          type="button"
                          onClick={() => handleOpenScenarioFromArchiv(entry)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <p className="truncate font-bold text-white text-base">{entry.titel}</p>
                          <p className="mt-1 text-xs text-slate-400">
                            {formatArchivDatum(entry.timestamp)}
                            {entry.summary ? (
                              <>
                                {' '}· Umsatz:{' '}
                                <span className="font-mono text-[#dcb68a]">
                                  {formatCurrency(entry.summary.monatsumsatzBrutto)}
                                </span>{' '}
                                · LF:{' '}
                                <span className="font-mono text-white">
                                  {fmtNum(entry.summary.lohnfaktorAktiv, 2)}
                                </span>{' '}
                                · Rentabilität:{' '}
                                <span className="font-mono text-emerald-400">
                                  {fmtPct(entry.summary.rentabilitaetPercent, 1)}
                                </span>
                              </>
                            ) : null}
                          </p>
                        </button>
                        <div className="flex shrink-0 flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleOpenScenarioFromArchiv(entry)}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-600 px-3 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-slate-800"
                          >
                            <FileText size={14} />
                            Öffnen
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowCreateForm(false);
                              setRenamingId(entry.id);
                              setRenameTitel(entry.titel);
                            }}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-600 px-3 py-2 text-xs font-bold uppercase tracking-wider text-[#dcb68a] hover:bg-slate-800"
                          >
                            <Pencil size={14} />
                            Umbenennen
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteScenarioFromArchiv(entry.id)}
                            className="inline-flex items-center gap-2 rounded-lg border border-red-900/50 px-3 py-2 text-xs font-bold uppercase tracking-wider text-red-400 hover:bg-red-950/30"
                          >
                            <Trash2 size={14} />
                            Löschen
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="peplan-shell min-h-screen bg-slate-950 text-slate-100 p-4 md:p-6 pb-32 font-sans">
      <style>{pePlanTooltipStyles}</style>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* ====================================================================
            1. HEADER & STATUS (MIT ARCHIV-ZURÜCK & SPEICHERN WIE BEI PE PROFIT)
            ==================================================================== */}
        <header className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 md:p-6 shadow-xl">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <button
                  type="button"
                  onClick={() => {
                    handleSaveCurrentScenario(true);
                    setViewMode('archive');
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-200 hover:border-[#dcb68a]/50 hover:text-[#dcb68a] transition-colors"
                >
                  <ArrowLeft size={14} />
                  Zurück zum Archiv
                </button>
                {isGodView ? (
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-lime-500/15 text-lime-400 border border-lime-500/40">
                    Gott-Modus · Arbeitstool
                  </span>
                ) : (
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-[#dcb68a]/15 text-[#dcb68a] border border-[#dcb68a]/40">
                    PEPlan Premium · Sandbox-Simulation
                  </span>
                )}
                <span className="text-xs font-mono text-slate-400">
                  Snapshot: {clientData?.firmenname || 'Mandant'} · Jahr {baseline.sourceYear || '—'} ({baseline.zeitraumMonate} Mon.)
                </span>
              </div>
              <h1 className="text-2xl md:text-3xl font-black tracking-tight text-white">
                {isGodView ? (
                  <>Planungsrechner</>
                ) : (
                  <>
                    <span className="text-[#dcb68a]">PEPlan</span> — Planungsrechner
                  </>
                )}
              </h1>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              {saveToast && (
                <span className="px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-xs font-bold text-emerald-300">
                  ✔ {saveToast}
                </span>
              )}

              <div
                className="flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-xl px-3 py-2"
                data-pe-tooltip="Vergib deinem Szenario einen Namen (z. B. ‚Neue Vollzeitkraft + 8 % Preisanpassung‘)."
                data-pe-tip-below=""
              >
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Szenario:
                </span>
                <input
                  type="text"
                  value={inputs.szenarioTitel}
                  onChange={(e) => setSzenarioTitel(e.target.value)}
                  className="bg-transparent text-xs font-bold text-[#dcb68a] outline-none w-40 sm:w-48"
                  placeholder="Szenario-Name"
                />
              </div>

              <button
                type="button"
                onClick={() => handleSaveCurrentScenario(false)}
                data-pe-tooltip="Speichert dieses Szenario im Archiv der Mandantenakte, sodass du es jederzeit wieder aufrufen oder vergleichen kannst."
                data-pe-tip-below=""
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#dcb68a] hover:bg-[#cfa878] text-xs font-black uppercase tracking-widest text-slate-950 transition-colors"
              >
                <Save size={14} />
                Im Archiv speichern
              </button>

              <button
                type="button"
                onClick={resetScenarioToBaseline}
                data-pe-tooltip="Setzt alle Regler wieder auf deine aktuellen Ist-Werte aus der Akte zurück. Deine echten Daten werden niemals verändert."
                data-pe-tip-below=""
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#dcb68a]/40 bg-[#dcb68a]/10 hover:bg-[#dcb68a]/20 text-xs font-black uppercase tracking-widest text-[#dcb68a] transition-colors"
              >
                <RotateCcw size={14} />
                Auf Baseline zurücksetzen
              </button>
            </div>
          </div>
        </header>

        {/* ====================================================================
            2 & 3. HAUPTBEREICH: LINKS STEUERUNGS-PANEL · RECHTS KERN-AUSWERTUNG
            (Exakt nach Layout-Logik PEProfitControlPanel + PEProfitReport)
            ==================================================================== */}
        <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-[minmax(0,430px)_minmax(0,1fr)] items-start">
          {/* ==================================================================
              2. STEUERUNGS-PANEL (LINKS, GEGLIEDERT IN SEKTIONEN / TABS)
              ================================================================== */}
          <aside className="min-w-0 rounded-2xl border border-slate-800 bg-slate-900/95 shadow-xl overflow-visible">
            <div className="border-b border-slate-800 px-5 py-4 bg-slate-950/60 rounded-t-2xl">
              <h2 className="text-sm font-black uppercase tracking-[0.18em] text-[#dcb68a]">
                Steuerungs-Cockpit
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Wähle einen Bereich — Änderungen wirken sofort im Report rechts.
              </p>
            </div>

            {/* Vertikale / klare Tab-Leiste für die 4 Steuerungsbereiche */}
            <div className="grid grid-cols-2 gap-1.5 p-3 bg-slate-950/40 border-b border-slate-800">
              <button
                type="button"
                onClick={() => setActiveTab('personal')}
                data-pe-tooltip="Neue Mitarbeiter einstellen, Stunden/Gehälter im Bestand anpassen oder Abgänge inkl. Auffangquote durchspielen."
                data-pe-tip-below=""
                className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider border transition-colors ${
                  activeTab === 'personal'
                    ? 'bg-[#dcb68a] text-slate-950 border-[#dcb68a]'
                    : 'bg-slate-900 text-slate-300 border-slate-800 hover:border-slate-700'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <Users size={14} className="shrink-0" /> Personal
                </span>
                {(activeHiresCount > 0 || activeDeparturesCount > 0) && (
                  <span className="px-1.5 py-0.2 rounded text-[10px] font-mono bg-black/20">
                    +{activeHiresCount}/−{activeDeparturesCount}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('inhaber')}
                data-pe-tooltip="Eigene Stuhlzeit (Bedienzeit), Umsatz-Auffangquote durch das Team und kalkulatorischen Unternehmerlohn planen."
                data-pe-tip-below=""
                className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider border transition-colors ${
                  activeTab === 'inhaber'
                    ? 'bg-[#dcb68a] text-slate-950 border-[#dcb68a]'
                    : 'bg-slate-900 text-slate-300 border-slate-800 hover:border-slate-700'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <Briefcase size={14} className="shrink-0" /> Inhaber
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('kosten')}
                data-pe-tooltip="Zusätzlichen Materialeinsatz (z. B. neue Pflegeserie), Fixkosten-Änderungen und Investitions-/Leasingraten simulieren."
                data-pe-tip-below=""
                className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider border transition-colors ${
                  activeTab === 'kosten'
                    ? 'bg-[#dcb68a] text-slate-950 border-[#dcb68a]'
                    : 'bg-slate-900 text-slate-300 border-slate-800 hover:border-slate-700'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <Wallet size={14} className="shrink-0" /> Kosten &amp; Invest
                </span>
                {activeInvCount > 0 && (
                  <span className="px-1.5 py-0.2 rounded text-[10px] font-mono bg-black/20">
                    {activeInvCount}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('preis')}
                data-pe-tooltip="Prozentuale Preisanpassung für den Salon und optionalen Kundenschwund testen."
                data-pe-tip-below=""
                className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider border transition-colors ${
                  activeTab === 'preis'
                    ? 'bg-[#dcb68a] text-slate-950 border-[#dcb68a]'
                    : 'bg-slate-900 text-slate-300 border-slate-800 hover:border-slate-700'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <TrendingUp size={14} className="shrink-0" /> Preisanpassung
                </span>
              </button>
            </div>

            <div className="p-5 space-y-6">
              {/* TAB 1: PERSONAL (ZUWACHS & BESTAND — INKL. PRODUKTIV, AZUBI, UNPRODUKTIV & STUNDENLOHN-MODUS) */}
              {activeTab === 'personal' && (
                <div className="space-y-6">
                  {/* Zuwachs */}
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-wider text-white flex items-center gap-1.5">
                        <UserPlus size={15} className="text-[#dcb68a]" /> Team-Zuwachs planen
                      </h3>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        AG-Nebenkosten: {fmtPct(globalSettings.lohnnebenkostenPercent, 1)} · Wochensatz: {fmtNum(globalSettings.wochensatz, 2)} Wo./Mon.
                      </p>
                    </div>

                    {/* 3 Direkt-Buttons für Produktiv, Azubi und Unproduktiv */}
                    <div className="grid grid-cols-3 gap-1.5">
                      <button
                        type="button"
                        onClick={() => addNewHire({ scope: 'produktiv' })}
                        data-pe-tooltip="Neue produktive Fachkraft am Stuhl einstellen (100 % prod. Lohnkosten, 100 % im Team-Stundenlohn, Standard-Umsatzfaktor 2,5×)."
                        className="inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg border border-[#dcb68a]/40 bg-[#dcb68a]/10 hover:bg-[#dcb68a]/20 text-[10px] font-bold uppercase tracking-wider text-[#dcb68a]"
                      >
                        <Plus size={12} /> Fachkraft
                      </button>
                      <button
                        type="button"
                        onClick={() => addNewHire({ scope: 'azubi' })}
                        data-pe-tooltip="Neuen Azubi einplanen (2/3 prod. Lohnkosten, 1/3 Gemeinkosten, fließt zu 2/3 in den Team-Stundenlohn ein)."
                        className="inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg border border-sky-500/40 bg-sky-500/10 hover:bg-sky-500/20 text-[10px] font-bold uppercase tracking-wider text-sky-300"
                      >
                        <Plus size={12} /> Azubi
                      </button>
                      <button
                        type="button"
                        onClick={() => addNewHire({ scope: 'unproduktiv' })}
                        data-pe-tooltip="Neue unproduktive Kraft einplanen, z. B. Rezeption oder Reinigung (100 % in die Gemeinkosten, kein eigener Stuhlumsatz)."
                        className="inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg border border-purple-500/40 bg-purple-500/10 hover:bg-purple-500/20 text-[10px] font-bold uppercase tracking-wider text-purple-300"
                      >
                        <Plus size={12} /> Unproduktiv
                      </button>
                    </div>

                    {inputs.zuwachs.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-800 bg-slate-950/50 p-4 text-xs text-slate-500">
                        Kein Neuzugang geplant. Wähle oben „+ Fachkraft“, „+ Azubi“ oder „+ Unproduktiv“.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {inputs.zuwachs.map((hire) => {
                          const scope = hire.scope || 'produktiv';
                          const wochensatz = globalSettings.wochensatz || 4.33;
                          const monatsstunden = Math.max(0.1, (hire.wochenstunden || 0) * wochensatz);
                          const isHourly = hire.lohnEingabeModus === 'stunde';
                          const currentHourly =
                            hire.stundenlohnBrutto !== undefined && hire.stundenlohnBrutto > 0
                              ? hire.stundenlohnBrutto
                              : Number((hire.bruttoGehaltMonat / monatsstunden).toFixed(2));
                          const agSatz =
                            hire.lohnnebenkostenSatzPercent ?? globalSettings.lohnnebenkostenPercent;
                          const agVollkosten = hire.bruttoGehaltMonat * (1 + agSatz / 100);

                          return (
                            <div
                              key={hire.id}
                              className="rounded-xl border border-slate-700 bg-slate-950/85 p-3.5 space-y-3"
                            >
                              {/* Kopfzeile OHNE überflüssige Checkbox — nur Name, Rolle & Papierkorb */}
                              <div className="flex items-center justify-between gap-2 border-b border-slate-800 pb-2.5">
                                <input
                                  type="text"
                                  value={hire.label}
                                  onChange={(e) =>
                                    updateNewHire(hire.id, { label: e.target.value })
                                  }
                                  className="bg-transparent text-xs font-black text-white border-b border-slate-700 focus:border-[#dcb68a] outline-none flex-1 min-w-0 py-0.5"
                                  placeholder="Bezeichnung eingeben"
                                />
                                <select
                                  value={scope}
                                  onChange={(e) => {
                                    const nextScope = e.target.value as 'produktiv' | 'azubi' | 'unproduktiv';
                                    updateNewHire(hire.id, {
                                      scope: nextScope,
                                      standardLohnfaktor: nextScope === 'produktiv' ? 2.5 : 0,
                                      umsatzAnrechnen: nextScope === 'produktiv',
                                    });
                                  }}
                                  className="rounded-lg border border-slate-700 bg-slate-900 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[#dcb68a] outline-none"
                                >
                                  <option value="produktiv">Produktiv</option>
                                  <option value="azubi">Azubi (⅔ Prod / ⅓ GK)</option>
                                  <option value="unproduktiv">Unproduktiv (100% GK)</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={() => removeNewHire(hire.id)}
                                  className="text-slate-500 hover:text-rose-400 p-1 shrink-0"
                                  title="Neuzugang löschen"
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>

                              {/* Info-Hinweis zur Kostenverteilung je nach Rolle */}
                              <div className="text-[11px] font-mono text-slate-400 bg-slate-900/70 rounded-lg px-2.5 py-1.5 border border-slate-800/80">
                                {scope === 'azubi' ? (
                                  <>
                                    AG-Vollkosten: <strong className="text-sky-300">{formatCurrency(agVollkosten)}</strong> →{' '}
                                    ⅔ Prod. ({formatCurrency(agVollkosten * (2 / 3))}) + ⅓ GK ({formatCurrency(agVollkosten * (1 / 3))}) · ⅔ im Team-Std.-Lohn
                                  </>
                                ) : scope === 'unproduktiv' ? (
                                  <>
                                    AG-Vollkosten: <strong className="text-purple-300">{formatCurrency(agVollkosten)}</strong> →{' '}
                                    fließt zu 100 % in die Gemeinkosten
                                  </>
                                ) : (
                                  <>
                                    AG-Vollkosten: <strong className="text-[#dcb68a]">{formatCurrency(agVollkosten)}</strong> →{' '}
                                    100 % prod. Lohnkosten &amp; Team-Std.-Lohn
                                  </>
                                )}
                              </div>

                              <NobleNumberControl
                                label="Wochenstunden"
                                tooltip="Vertragliche Arbeitsstunden pro Woche (bei Azubis abzüglich Berufsschulzeit)."
                                value={hire.wochenstunden}
                                onChange={(newWStd) => {
                                  if (isHourly) {
                                    const nextBrutto = Math.round(currentHourly * newWStd * wochensatz);
                                    updateNewHire(hire.id, {
                                      wochenstunden: newWStd,
                                      bruttoGehaltMonat: nextBrutto,
                                    });
                                  } else {
                                    updateNewHire(hire.id, { wochenstunden: newWStd });
                                  }
                                }}
                                min={1}
                                max={45}
                                step={1}
                                suffix="Std"
                                showSlider={false}
                              />

                              {/* Umschalter: Monatsbrutto vs. Stundenlohn */}
                              <div className="space-y-1">
                                <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                  Lohn eingeben als:
                                </span>
                                <div className="grid grid-cols-2 gap-1.5 rounded-lg border border-slate-800 bg-slate-900 p-1">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateNewHire(hire.id, { lohnEingabeModus: 'monat' })
                                    }
                                    className={`rounded-md py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                                      !isHourly
                                        ? 'bg-[#dcb68a] text-slate-950'
                                        : 'text-slate-400 hover:text-white'
                                    }`}
                                  >
                                    Monatsbrutto
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      updateNewHire(hire.id, {
                                        lohnEingabeModus: 'stunde',
                                        stundenlohnBrutto: Number(
                                          (hire.bruttoGehaltMonat / monatsstunden).toFixed(2)
                                        ),
                                      })
                                    }
                                    className={`rounded-md py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors ${
                                      isHourly
                                        ? 'bg-[#dcb68a] text-slate-950'
                                        : 'text-slate-400 hover:text-white'
                                    }`}
                                  >
                                    Stundenlohn
                                  </button>
                                </div>
                              </div>

                              {isHourly ? (
                                <>
                                  <NobleNumberControl
                                    label="Stundenlohn (Brutto / Std.)"
                                    hint={`Rechnet automatisch hoch: × ${fmtNum(hire.wochenstunden, 1)} Std. × ${fmtNum(wochensatz, 2)} Wo.`}
                                    tooltip="Gib den Brutto-Stundenlohn ein — das monatliche Bruttogehalt wird automatisch über die Wochenstunden hochgerechnet."
                                    value={currentHourly}
                                    onChange={(newHourly) => {
                                      const nextBrutto = Math.round(newHourly * monatsstunden);
                                      updateNewHire(hire.id, {
                                        stundenlohnBrutto: newHourly,
                                        bruttoGehaltMonat: nextBrutto,
                                      });
                                    }}
                                    min={0}
                                    max={60}
                                    step={0.5}
                                    decimals={2}
                                    suffix={`${currencyCode}/h`}
                                    showSlider={false}
                                  />
                                  <div className="flex items-center justify-between text-xs font-mono text-slate-300 px-1">
                                    <span>Hochgerechnetes Monatsbrutto:</span>
                                    <strong className="text-[#dcb68a]">
                                      = {formatCurrency(hire.bruttoGehaltMonat)} / Monat
                                    </strong>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <NobleNumberControl
                                    label={
                                      scope === 'azubi'
                                        ? 'Azubi-Vergütung (Brutto / Monat)'
                                        : 'Bruttogehalt pro Monat'
                                    }
                                    tooltip="Monatliches Bruttogehalt. Arbeitgeber-Nebenkosten werden automatisch aufgeschlagen."
                                    value={hire.bruttoGehaltMonat}
                                    onChange={(v) =>
                                      updateNewHire(hire.id, {
                                        bruttoGehaltMonat: v,
                                        stundenlohnBrutto: Number((v / monatsstunden).toFixed(2)),
                                      })
                                    }
                                    min={0}
                                    max={8000}
                                    step={50}
                                    suffix={currencyCode}
                                    showSlider={false}
                                  />
                                  <div className="flex items-center justify-between text-xs font-mono text-slate-400 px-1">
                                    <span>Entspricht Stundenlohn (Brutto):</span>
                                    <strong className="text-slate-200">
                                      = {formatCurrency(hire.bruttoGehaltMonat / monatsstunden, 2)} / Std.
                                    </strong>
                                  </div>
                                </>
                              )}

                              <label className="flex items-center justify-between gap-3 rounded-lg border border-emerald-500/30 bg-emerald-950/20 px-3 py-2 text-xs text-emerald-200">
                                <span className="font-bold uppercase tracking-wider">Förderung (EGZ)</span>
                                <input
                                  type="checkbox"
                                  checked={hire.isGefoerdert === true}
                                  onChange={(e) =>
                                    updateNewHire(hire.id, {
                                      isGefoerdert: e.target.checked,
                                      foerderquoteProzent:
                                        Number(hire.foerderquoteProzent) > 0
                                          ? hire.foerderquoteProzent
                                          : 50,
                                    })
                                  }
                                  className="h-4 w-4 accent-emerald-400"
                                />
                              </label>
                              {hire.isGefoerdert === true && (
                                <NobleNumberControl
                                  label="Förderquote"
                                  tooltip="Prozent vom ungekürzten Bruttogehalt. Wird nur am Ende vom Break-even abgezogen."
                                  value={Number(hire.foerderquoteProzent) > 0 ? Number(hire.foerderquoteProzent) : 50}
                                  onChange={(v) =>
                                    updateNewHire(hire.id, {
                                      isGefoerdert: true,
                                      foerderquoteProzent: v,
                                    })
                                  }
                                  min={1}
                                  max={100}
                                  step={5}
                                  suffix="%"
                                  showSlider={false}
                                />
                              )}

                              {scope !== 'unproduktiv' && (
                                <>
                                  <NobleNumberControl
                                    label={
                                      scope === 'azubi'
                                        ? 'Optionaler Azubi-Umsatzfaktor'
                                        : 'Umsatz-Faktor (Standard 2,5×)'
                                    }
                                    hint="Faktor × Bruttogehalt wird auf den Gesamtumsatz addiert"
                                    tooltip="Mit welchem Faktor spielt die Kraft ihr Bruttogehalt als Brutto-Umsatz ein? Standard für neue Fachkräfte ist 2,5×."
                                    value={hire.standardLohnfaktor}
                                    onChange={(v) =>
                                      updateNewHire(hire.id, { standardLohnfaktor: v })
                                    }
                                    min={0}
                                    max={6.0}
                                    step={0.1}
                                    decimals={2}
                                    suffix="×"
                                    showSlider={false}
                                  />
                                  <label
                                    className="flex items-center justify-between gap-2 pt-2 border-t border-slate-800/80 text-xs cursor-pointer"
                                    data-pe-tooltip="Wenn aktiviert, wird der erwartete Umsatz oben auf den Salon-Gesamtumsatz angerechnet (inkl. Materialverbrauch)."
                                  >
                                    <span className="flex items-center gap-2 text-slate-300">
                                      <input
                                        type="checkbox"
                                        checked={hire.umsatzAnrechnen !== false}
                                        onChange={(e) =>
                                          updateNewHire(hire.id, { umsatzAnrechnen: e.target.checked })
                                        }
                                        className="h-4 w-4 accent-[#dcb68a]"
                                      />
                                      Umsatz auf Gesamtumsatz anrechnen
                                    </span>
                                    <span className="font-mono font-bold text-emerald-400">
                                      {hire.umsatzAnrechnen !== false
                                        ? `+${formatCurrency(hire.bruttoGehaltMonat * hire.standardLohnfaktor)} brutto`
                                        : '0 €'}
                                    </span>
                                  </label>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Bestandsmitarbeiter: 3 auf- und zuklappbare Rubriken + einzeln aufklappbare Mitarbeiter */}
                  <div className="space-y-3 pt-4 border-t border-slate-800">
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-wider text-white flex items-center gap-1.5">
                        <UserMinus size={15} className="text-[#dcb68a]" /> Bestands-Team ändern
                      </h3>
                      <p className="text-[11px] text-slate-400">
                        Rubrik aufklappen und Mitarbeiter anklicken, um Stunden, Lohn, Rolle oder Abgang zu planen.
                      </p>
                    </div>

                    {baseline.employees.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-800 bg-slate-950/50 p-4 text-xs text-slate-500">
                        Keine Mitarbeiter in der Akte hinterlegt.
                      </div>
                    ) : (
                      (() => {
                        const getBaseScope = (
                          m: (typeof baseline.employees)[number]
                        ): 'produktiv' | 'azubi' | 'unproduktiv' => {
                          if (m.scope === 'azubi') {
                            return 'azubi';
                          }
                          if (m.scope === 'unproduktiv') return 'unproduktiv';
                          return 'produktiv';
                        };

                        const groupsConfig: {
                          key: 'produktiv' | 'azubi' | 'unproduktiv';
                          title: string;
                          accentBorder: string;
                          badgeClass: string;
                          list: typeof baseline.employees;
                        }[] = [
                          {
                            key: 'produktiv',
                            title: 'Produktive Mitarbeiter',
                            accentBorder: 'border-l-[#dcb68a]',
                            badgeClass: 'bg-[#dcb68a]/15 text-[#dcb68a] border-[#dcb68a]/40',
                            list: baseline.employees.filter((m) => getBaseScope(m) === 'produktiv'),
                          },
                          {
                            key: 'azubi',
                            title: 'Azubis',
                            accentBorder: 'border-l-sky-400',
                            badgeClass: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
                            list: baseline.employees.filter((m) => getBaseScope(m) === 'azubi'),
                          },
                          {
                            key: 'unproduktiv',
                            title: 'Unproduktive & Assistenzkräfte',
                            accentBorder: 'border-l-purple-400',
                            badgeClass: 'bg-purple-500/15 text-purple-300 border-purple-500/40',
                            list: baseline.employees.filter((m) => getBaseScope(m) === 'unproduktiv'),
                          },
                        ];

                        return (
                          <div className="space-y-2.5">
                            {groupsConfig.map((grp) => {
                              const isOpen = openBestandGroups[grp.key];
                              const changedCount = grp.list.filter((ma) => {
                                const dep = inputs.abgaenge.find((d) => d.employeeId === ma.id);
                                if (!dep) return false;
                                const baseScope = getBaseScope(ma);
                                const effScope = dep.customScope || baseScope;
                                const effWStd = dep.customWochenstunden ?? ma.wochenstunden;
                                const effBrutto = dep.customBruttoGehaltMonat ?? ma.grundgehaltBrutto;
                                return (
                                  dep.aktiv === true ||
                                  effScope !== baseScope ||
                                  Math.abs(effWStd - ma.wochenstunden) >= 0.05 ||
                                  Math.abs(effBrutto - ma.grundgehaltBrutto) >= 0.5
                                );
                              }).length;

                              return (
                                <div
                                  key={grp.key}
                                  className={`rounded-xl border border-slate-800 border-l-4 ${grp.accentBorder} bg-slate-950/60 overflow-hidden`}
                                >
                                  {/* Rubrik-Kopfzeile (Auf-/Zuklappbar) */}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setOpenBestandGroups((prev) => ({
                                        ...prev,
                                        [grp.key]: !prev[grp.key],
                                      }))
                                    }
                                    className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 text-left hover:bg-slate-900/70 transition-colors"
                                  >
                                    <div className="flex items-center gap-2 min-w-0">
                                      <span className="text-xs font-black uppercase tracking-wider text-white truncate">
                                        {grp.title}
                                      </span>
                                      <span
                                        className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold border ${grp.badgeClass}`}
                                      >
                                        {grp.list.length}
                                      </span>
                                      {changedCount > 0 && (
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-500/20 text-amber-300 border border-amber-500/40">
                                          {changedCount} geändert
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0">
                                      <span>{isOpen ? 'Ausblenden' : 'Einblenden'}</span>
                                      {isOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                                    </div>
                                  </button>

                                  {/* Inhalt der Rubrik */}
                                  {isOpen && (
                                    <div className="p-2.5 pt-1 border-t border-slate-800/80 space-y-2 max-h-[420px] overflow-y-auto">
                                      {grp.list.length === 0 ? (
                                        <div className="text-xs text-slate-500 py-3 text-center">
                                          Keine Mitarbeiter in dieser Rubrik.
                                        </div>
                                      ) : (
                                        grp.list.map((ma) => {
                                          const dep = inputs.abgaenge.find(
                                            (d) => d.employeeId === ma.id
                                          );
                                          const isDeparted = dep?.aktiv === true;
                                          const auffang = dep?.auffangQuotePercent ?? 50;
                                          const baseScope = getBaseScope(ma);
                                          const effScope: 'produktiv' | 'azubi' | 'unproduktiv' =
                                            dep?.customScope || baseScope;
                                          const isPromotedToProd =
                                            baseScope !== 'produktiv' && effScope === 'produktiv';
                                          const effWStd = dep?.customWochenstunden ?? ma.wochenstunden;
                                          const effBrutto =
                                            dep?.customBruttoGehaltMonat ?? ma.grundgehaltBrutto;
                                          const wochensatz = globalSettings.wochensatz || 4.33;
                                          const effMonatsstunden = Math.max(0.1, effWStd * wochensatz);
                                          const isHourly = dep?.lohnEingabeModus === 'stunde';
                                          const currentHourly =
                                            dep?.customStundenlohnBrutto !== undefined &&
                                            dep.customStundenlohnBrutto > 0
                                              ? dep.customStundenlohnBrutto
                                              : Number((effBrutto / effMonatsstunden).toFixed(2));
                                          const hoursReduced =
                                            !isDeparted &&
                                            baseScope === 'produktiv' &&
                                            (effScope !== 'produktiv' ||
                                              effWStd < ma.wochenstunden - 0.05);
                                          const isModified =
                                            isDeparted ||
                                            effScope !== baseScope ||
                                            Math.abs(effWStd - ma.wochenstunden) >= 0.05 ||
                                            Math.abs(effBrutto - ma.grundgehaltBrutto) >= 0.5;
                                          const isExpanded = expandedEmployeeId === ma.id;

                                          // Verknüpfung zwischen produktivem Mitarbeiter und seinem 'Unprod. Anteil'
                                          const linkedProdMa =
                                            baseScope === 'unproduktiv' &&
                                            ma.taetigkeit === 'Unprod. Anteil'
                                              ? baseline.employees.find(
                                                  (e) =>
                                                    e.scope === 'produktiv' &&
                                                    (e.id === ma.linkedProductiveEmployeeId ||
                                                      e.name.trim().toLowerCase() ===
                                                        ma.name.trim().toLowerCase())
                                                )
                                              : undefined;

                                          const linkedUnprodAnteilMa =
                                            baseScope === 'produktiv'
                                              ? baseline.employees.find(
                                                  (e) =>
                                                    e.scope === 'unproduktiv' &&
                                                    e.taetigkeit === 'Unprod. Anteil' &&
                                                    (e.linkedProductiveEmployeeId === ma.id ||
                                                      e.name.trim().toLowerCase() ===
                                                        ma.name.trim().toLowerCase())
                                                )
                                              : undefined;

                                          const linkedUnprodDep = linkedUnprodAnteilMa
                                            ? inputs.abgaenge.find(
                                                (d) => d.employeeId === linkedUnprodAnteilMa.id
                                              )
                                            : undefined;
                                          const unprodAnteilEffWStd = linkedUnprodAnteilMa
                                            ? linkedUnprodDep?.aktiv
                                              ? 0
                                              : (linkedUnprodDep?.customWochenstunden ??
                                                linkedUnprodAnteilMa.wochenstunden)
                                            : 0;
                                          const unprodAnteilEffBrutto = linkedUnprodAnteilMa
                                            ? linkedUnprodDep?.aktiv
                                              ? 0
                                              : (linkedUnprodDep?.customBruttoGehaltMonat ??
                                                linkedUnprodAnteilMa.grundgehaltBrutto)
                                            : 0;
                                          const isUnprodSplitCoupled = linkedUnprodAnteilMa
                                            ? linkedUnprodDep?.splitCoupled !== false
                                            : dep?.splitCoupled !== false;

                                          /**
                                           * Schichtet Änderungen am 'Unprod. Anteil' gegenläufig in die produktiven
                                           * Stunden & das produktive Gehalt des verknüpften Mitarbeiters um
                                           * (sofern gekoppelt; wenn auf 'Frei' gestellt, wird nur der unproduktive
                                           * Eintrag selbst geändert, ohne die andere Seite anzufassen).
                                           */
                                          const applyUnprodAnteilTransfer = (
                                            unprodEmployee: typeof ma,
                                            prodEmployee: typeof ma,
                                            nextUnprodWStd: number,
                                            nextUnprodBrutto: number
                                          ) => {
                                            const prevUnprodDep = inputs.abgaenge.find(
                                              (d) => d.employeeId === unprodEmployee.id
                                            );
                                            const coupled = prevUnprodDep?.splitCoupled !== false;
                                            const prevUnprodWStd =
                                              prevUnprodDep?.customWochenstunden ??
                                              unprodEmployee.wochenstunden;
                                            const prevUnprodBrutto =
                                              prevUnprodDep?.customBruttoGehaltMonat ??
                                              unprodEmployee.grundgehaltBrutto;

                                            const deltaUnprodWStd = nextUnprodWStd - prevUnprodWStd;
                                            const deltaUnprodBrutto =
                                              nextUnprodBrutto - prevUnprodBrutto;

                                            setDeparture(unprodEmployee.id, {
                                              aktiv: false,
                                              customWochenstunden: Math.max(0, nextUnprodWStd),
                                              customBruttoGehaltMonat: Math.max(
                                                0,
                                                Math.round(nextUnprodBrutto)
                                              ),
                                            });

                                            if (!coupled) return;

                                            const prodDep = inputs.abgaenge.find(
                                              (d) => d.employeeId === prodEmployee.id
                                            );
                                            const curProdWStd =
                                              prodDep?.customWochenstunden ??
                                              prodEmployee.wochenstunden;
                                            const curProdBrutto =
                                              prodDep?.customBruttoGehaltMonat ??
                                              prodEmployee.grundgehaltBrutto;

                                            const newProdWStd = Math.max(
                                              0,
                                              Number((curProdWStd - deltaUnprodWStd).toFixed(2))
                                            );
                                            const newProdBrutto = Math.max(
                                              0,
                                              Math.round(curProdBrutto - deltaUnprodBrutto)
                                            );
                                            const newProdMonatsstd = Math.max(
                                              0.1,
                                              newProdWStd * wochensatz
                                            );

                                            setDeparture(prodEmployee.id, {
                                              customWochenstunden: newProdWStd,
                                              customBruttoGehaltMonat: newProdBrutto,
                                              customStundenlohnBrutto: Number(
                                                (newProdBrutto / newProdMonatsstd).toFixed(2)
                                              ),
                                            });
                                          };

                                          return (
                                            <div
                                              key={ma.id}
                                              className={`rounded-xl border transition-colors ${
                                                isDeparted
                                                  ? 'bg-rose-950/20 border-rose-500/40'
                                                  : isPromotedToProd
                                                    ? 'bg-emerald-950/15 border-emerald-500/40'
                                                    : isModified
                                                      ? 'bg-amber-950/15 border-amber-500/40'
                                                      : 'bg-slate-900/80 border-slate-800'
                                              }`}
                                            >
                                              {/* Kompakte 1-Zeilen-Ansicht für jeden Mitarbeiter */}
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  setExpandedEmployeeId(isExpanded ? null : ma.id)
                                                }
                                                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left"
                                              >
                                                <div className="min-w-0 flex-1">
                                                  <div className="flex flex-wrap items-center gap-1.5">
                                                    <span className="text-xs font-bold text-white truncate">
                                                      {ma.name}
                                                    </span>
                                                    {isDeparted && (
                                                      <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-rose-500/20 text-rose-300">
                                                        Abgang
                                                      </span>
                                                    )}
                                                    {!isDeparted && isPromotedToProd && (
                                                      <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-emerald-500/20 text-emerald-300">
                                                        → Fachkraft
                                                      </span>
                                                    )}
                                                    {!isDeparted && !isPromotedToProd && isModified && (
                                                      <span className="px-1.5 py-0.5 rounded text-[9px] font-black uppercase bg-amber-500/20 text-amber-300">
                                                        Angepasst
                                                      </span>
                                                    )}
                                                  </div>
                                                  <span className="text-[10px] font-mono text-slate-400 block mt-0.5">
                                                    {isDeparted ? (
                                                      <>Ausgeschieden (Auffangquote: {auffang} %)</>
                                                    ) : (
                                                      <>
                                                        {fmtNum(effWStd, 0)} Std./Wo. · Brutto:{' '}
                                                        <strong className="text-slate-200">
                                                          {formatCurrency(effBrutto)}
                                                        </strong>
                                                      </>
                                                    )}
                                                  </span>
                                                </div>
                                                <div className="flex items-center gap-1 text-[10px] font-bold uppercase text-[#dcb68a] shrink-0">
                                                  <span>{isExpanded ? 'Schließen' : 'Bearbeiten'}</span>
                                                  {isExpanded ? (
                                                    <ChevronUp size={14} />
                                                  ) : (
                                                    <ChevronDown size={14} />
                                                  )}
                                                </div>
                                              </button>

                                              {/* Aufgeklappte Detail-Steuerung nur für den angeklickten Mitarbeiter */}
                                              {isExpanded && (
                                                <div className="px-3 pb-3 pt-2 border-t border-slate-800/80 space-y-2.5">
                                                  <div className="flex items-center justify-between gap-2">
                                                    <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                                                      <input
                                                        type="checkbox"
                                                        checked={isDeparted}
                                                        onChange={(e) =>
                                                          setDeparture(ma.id, {
                                                            aktiv: e.target.checked,
                                                          })
                                                        }
                                                        className="h-4 w-4 accent-[#dcb68a]"
                                                      />
                                                      Mitarbeiter verlässt den Salon (Abgang)
                                                    </label>
                                                      {isModified && (
                                                      <button
                                                        type="button"
                                                        onClick={() => {
                                                          setDeparture(ma.id, {
                                                            aktiv: false,
                                                            customScope: baseScope,
                                                            customWochenstunden: ma.wochenstunden,
                                                            customBruttoGehaltMonat:
                                                              ma.grundgehaltBrutto,
                                                            lohnEingabeModus: 'monat',
                                                          });
                                                          if (linkedUnprodAnteilMa) {
                                                            setDeparture(linkedUnprodAnteilMa.id, {
                                                              aktiv: false,
                                                              customWochenstunden:
                                                                linkedUnprodAnteilMa.wochenstunden,
                                                              customBruttoGehaltMonat:
                                                                linkedUnprodAnteilMa.grundgehaltBrutto,
                                                              lohnEingabeModus: 'monat',
                                                            });
                                                          }
                                                        }}
                                                        className="text-[10px] font-bold text-[#dcb68a] hover:underline"
                                                      >
                                                        Auf IST zurücksetzen
                                                      </button>
                                                    )}
                                                  </div>

                                                  {!isDeparted && (
                                                    <>
                                                      {/* Falls dieser produktive MA einen 'Unprod. Anteil' hat: Direkt-Steuerung zum Umschichten in Produktivität */}
                                                      {linkedUnprodAnteilMa && (
                                                        <div className="rounded-lg border border-[#dcb68a]/35 bg-[#dcb68a]/10 p-2.5 space-y-2">
                                                          <div className="flex flex-wrap items-center justify-between gap-2">
                                                            <span className="text-[10px] font-black uppercase tracking-wider text-[#dcb68a]">
                                                              Unprod. Anteil (IST:{' '}
                                                              {fmtNum(
                                                                linkedUnprodAnteilMa.wochenstunden,
                                                                1
                                                              )}{' '}
                                                              Std. /{' '}
                                                              {formatCurrency(
                                                                linkedUnprodAnteilMa.grundgehaltBrutto
                                                              )}
                                                              )
                                                            </span>
                                                            <button
                                                              type="button"
                                                              onClick={() =>
                                                                setDeparture(
                                                                  linkedUnprodAnteilMa.id,
                                                                  {
                                                                    splitCoupled:
                                                                      !isUnprodSplitCoupled,
                                                                  }
                                                                )
                                                              }
                                                              className={`rounded px-2 py-0.5 text-[9px] font-black uppercase tracking-wider transition-colors ${
                                                                isUnprodSplitCoupled
                                                                  ? 'bg-[#dcb68a] text-slate-950'
                                                                  : 'border border-slate-600 bg-slate-900 text-slate-300 hover:text-white'
                                                              }`}
                                                            >
                                                              {isUnprodSplitCoupled
                                                                ? 'Kopplung: AN (Umschichten)'
                                                                : 'Kopplung: AUS (Frei)'}
                                                            </button>
                                                          </div>
                                                          <p className="text-[10px] text-slate-300 leading-snug">
                                                            {isUnprodSplitCoupled
                                                              ? 'Kopplung AN: Reduzierst du den unproduktiven Anteil, wandern Stunden & Lohn automatisch in die Produktivität. Schalte auf „Frei“, um die aktuellen Werte einzufrieren und unabhängig zu ändern.'
                                                              : 'Kopplung AUS (Frei): Aktuelle Werte sind fixiert — du kannst unproduktiv und produktiv völlig unabhängig voneinander erhöhen oder senken.'}
                                                          </p>
                                                          <NobleNumberControl
                                                            label="Unprod. Wochenstunden (Rest)"
                                                            value={unprodAnteilEffWStd}
                                                            onChange={(nextUnprodW) => {
                                                              const unprodHourly =
                                                                linkedUnprodAnteilMa.wochenstunden >
                                                                  0 && wochensatz > 0
                                                                  ? linkedUnprodAnteilMa.grundgehaltBrutto /
                                                                    (linkedUnprodAnteilMa.wochenstunden *
                                                                      wochensatz)
                                                                  : currentHourly;
                                                              const nextUnprodBrutto =
                                                                unprodHourly > 0
                                                                  ? Math.round(
                                                                      unprodHourly *
                                                                        nextUnprodW *
                                                                        wochensatz
                                                                    )
                                                                  : unprodAnteilEffBrutto;
                                                              applyUnprodAnteilTransfer(
                                                                linkedUnprodAnteilMa,
                                                                ma,
                                                                nextUnprodW,
                                                                nextUnprodBrutto
                                                              );
                                                            }}
                                                            min={0}
                                                            max={48}
                                                            step={1}
                                                            suffix="Std"
                                                            showSlider={false}
                                                          />
                                                          <NobleNumberControl
                                                            label="Unprod. Bruttolohn (Rest / Monat)"
                                                            value={unprodAnteilEffBrutto}
                                                            onChange={(nextUnprodB) => {
                                                              const ratio =
                                                                linkedUnprodAnteilMa.grundgehaltBrutto >
                                                                0
                                                                  ? nextUnprodB /
                                                                    linkedUnprodAnteilMa.grundgehaltBrutto
                                                                  : 0;
                                                              const nextUnprodW =
                                                                linkedUnprodAnteilMa.grundgehaltBrutto >
                                                                0
                                                                  ? Number(
                                                                      (
                                                                        linkedUnprodAnteilMa.wochenstunden *
                                                                        ratio
                                                                      ).toFixed(1)
                                                                    )
                                                                  : unprodAnteilEffWStd;
                                                              applyUnprodAnteilTransfer(
                                                                linkedUnprodAnteilMa,
                                                                ma,
                                                                nextUnprodW,
                                                                nextUnprodB
                                                              );
                                                            }}
                                                            min={0}
                                                            max={8000}
                                                            step={100}
                                                            suffix={currencyCode}
                                                            showSlider={false}
                                                          />
                                                        </div>
                                                      )}
                                                      {/* Rollenwechsel (z. B. Azubi -> Fachkraft / Geselle übernehmen) */}
                                                      {linkedProdMa && (
                                                        <div className="flex items-center justify-between gap-2 rounded-lg border border-[#dcb68a]/35 bg-[#dcb68a]/10 px-2.5 py-1.5">
                                                          <span className="text-[10px] font-bold text-[#dcb68a]">
                                                            Verknüpft mit {linkedProdMa.name} (Produktiv)
                                                          </span>
                                                          <button
                                                            type="button"
                                                            onClick={() =>
                                                              setDeparture(ma.id, {
                                                                splitCoupled: !isUnprodSplitCoupled,
                                                              })
                                                            }
                                                            className={`rounded px-2 py-0.5 text-[9px] font-black uppercase tracking-wider transition-colors ${
                                                              isUnprodSplitCoupled
                                                                ? 'bg-[#dcb68a] text-slate-950'
                                                                : 'border border-slate-600 bg-slate-900 text-slate-300 hover:text-white'
                                                            }`}
                                                          >
                                                            {isUnprodSplitCoupled
                                                              ? 'Kopplung: AN (Abzug)'
                                                              : 'Kopplung: AUS (Frei)'}
                                                          </button>
                                                        </div>
                                                      )}
                                                      <div className="flex items-center justify-between gap-2 pt-1">
                                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                                          Rolle im Szenario:
                                                        </span>
                                                        <select
                                                          value={effScope}
                                                          onChange={(e) => {
                                                            const nextScope = e.target.value as
                                                              | 'produktiv'
                                                              | 'azubi'
                                                              | 'unproduktiv';
                                                            const switchingToProd =
                                                              baseScope !== 'produktiv' &&
                                                              nextScope === 'produktiv';
                                                            setDeparture(ma.id, {
                                                              customScope: nextScope,
                                                              customWochenstunden:
                                                                switchingToProd &&
                                                                effWStd === ma.wochenstunden
                                                                  ? 38
                                                                  : effWStd,
                                                              customBruttoGehaltMonat:
                                                                switchingToProd &&
                                                                effBrutto === ma.grundgehaltBrutto
                                                                  ? 2400
                                                                  : effBrutto,
                                                              customUmsatzFaktor:
                                                                dep?.customUmsatzFaktor ??
                                                                (switchingToProd ? 2.5 : 0),
                                                              customUmsatzAnrechnen:
                                                                dep?.customUmsatzAnrechnen ??
                                                                switchingToProd,
                                                            });
                                                          }}
                                                          className="rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-[#dcb68a] outline-none focus:border-[#dcb68a]"
                                                        >
                                                          <option value="produktiv">
                                                            Produktiv (Fachkraft / Geselle)
                                                          </option>
                                                          <option value="azubi">
                                                            Azubi (⅔ Prod / ⅓ GK)
                                                          </option>
                                                          <option value="unproduktiv">
                                                            Unproduktiv / Assistenz (100% GK)
                                                          </option>
                                                        </select>
                                                      </div>

                                                      <NobleNumberControl
                                                        label="Geplante Wochenstunden"
                                                        value={effWStd}
                                                        onChange={(newWStd) => {
                                                          // Proportional: Wenn die Stunden angepasst werden (und das Gehalt noch nicht separat editiert wurde),
                                                          // skaliert das Monatsbrutto proportional mit dem aktuellen/bisherigen Stundenlohn!
                                                          const baseHourly =
                                                            ma.wochenstunden > 0 && wochensatz > 0
                                                              ? ma.grundgehaltBrutto /
                                                                (ma.wochenstunden * wochensatz)
                                                              : currentHourly;
                                                          const effectiveHourly = isHourly
                                                            ? currentHourly
                                                            : Math.abs(effBrutto - ma.grundgehaltBrutto) >= 0.5 &&
                                                                effWStd > 0 &&
                                                                wochensatz > 0
                                                              ? effBrutto / (effWStd * wochensatz)
                                                              : baseHourly;
                                                          const nextBrutto =
                                                            effectiveHourly > 0
                                                              ? Math.round(
                                                                  effectiveHourly * newWStd * wochensatz
                                                                )
                                                              : effBrutto;
                                                          if (linkedProdMa) {
                                                            applyUnprodAnteilTransfer(
                                                              ma,
                                                              linkedProdMa,
                                                              newWStd,
                                                              nextBrutto
                                                            );
                                                            return;
                                                          }
                                                          setDeparture(ma.id, {
                                                            customWochenstunden: newWStd,
                                                            customBruttoGehaltMonat: nextBrutto,
                                                          });
                                                        }}
                                                        min={0}
                                                        max={48}
                                                        step={1}
                                                        suffix="Std"
                                                        showSlider={false}
                                                      />

                                                      {/* Umschalter Monatsbrutto vs. Stundenlohn */}
                                                      <div className="grid grid-cols-2 gap-1 rounded-lg border border-slate-800 bg-slate-950 p-1">
                                                        <button
                                                          type="button"
                                                          onClick={() =>
                                                            setDeparture(ma.id, {
                                                              lohnEingabeModus: 'monat',
                                                            })
                                                          }
                                                          className={`rounded py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                                                            !isHourly
                                                              ? 'bg-[#dcb68a] text-slate-950'
                                                              : 'text-slate-400 hover:text-white'
                                                          }`}
                                                        >
                                                          Monatsbrutto
                                                        </button>
                                                        <button
                                                          type="button"
                                                          onClick={() =>
                                                            setDeparture(ma.id, {
                                                              lohnEingabeModus: 'stunde',
                                                              customStundenlohnBrutto: Number(
                                                                (
                                                                  effBrutto / effMonatsstunden
                                                                ).toFixed(2)
                                                              ),
                                                            })
                                                          }
                                                          className={`rounded py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                                                            isHourly
                                                              ? 'bg-[#dcb68a] text-slate-950'
                                                              : 'text-slate-400 hover:text-white'
                                                          }`}
                                                        >
                                                          Stundenlohn
                                                        </button>
                                                      </div>

                                                      {isHourly ? (
                                                        <>
                                                          <NobleNumberControl
                                                            label="Geplanter Stundenlohn (Brutto / Std.)"
                                                            value={currentHourly}
                                                            onChange={(newHourly) => {
                                                              const nextBrutto = Math.round(
                                                                newHourly * effMonatsstunden
                                                              );
                                                              setDeparture(ma.id, {
                                                                customStundenlohnBrutto: newHourly,
                                                                customBruttoGehaltMonat: nextBrutto,
                                                              });
                                                            }}
                                                            min={0}
                                                            max={60}
                                                            step={0.5}
                                                            decimals={2}
                                                            suffix={`${currencyCode}/h`}
                                                            showSlider={false}
                                                          />
                                                          <div className="flex items-center justify-between text-[11px] font-mono text-slate-300 px-1">
                                                            <span>Hochgerechnetes Monatsbrutto:</span>
                                                            <strong className="text-[#dcb68a]">
                                                              = {formatCurrency(effBrutto)} / Monat
                                                            </strong>
                                                          </div>
                                                        </>
                                                      ) : (
                                                        <>
                                                          <NobleNumberControl
                                                            label="Geplantes Bruttogehalt pro Monat"
                                                            value={effBrutto}
                                                            onChange={(v) => {
                                                              if (linkedProdMa) {
                                                                const ratio =
                                                                  ma.grundgehaltBrutto > 0
                                                                    ? v / ma.grundgehaltBrutto
                                                                    : 0;
                                                                const nextW =
                                                                  ma.grundgehaltBrutto > 0
                                                                    ? Number(
                                                                        (
                                                                          ma.wochenstunden * ratio
                                                                        ).toFixed(1)
                                                                      )
                                                                    : effWStd;
                                                                applyUnprodAnteilTransfer(
                                                                  ma,
                                                                  linkedProdMa,
                                                                  nextW,
                                                                  v
                                                                );
                                                                return;
                                                              }
                                                              setDeparture(ma.id, {
                                                                customBruttoGehaltMonat: v,
                                                                customStundenlohnBrutto: Number(
                                                                  (v / effMonatsstunden).toFixed(2)
                                                                ),
                                                              });
                                                            }}
                                                            min={0}
                                                            max={8000}
                                                            step={100}
                                                            suffix={currencyCode}
                                                            showSlider={false}
                                                          />
                                                          <div className="flex items-center justify-between text-[11px] font-mono text-slate-400 px-1">
                                                            <span>Entspricht Stundenlohn:</span>
                                                            <strong className="text-slate-300">
                                                              ={' '}
                                                              {formatCurrency(
                                                                effBrutto / effMonatsstunden,
                                                                2
                                                              )}{' '}
                                                              / Std.
                                                            </strong>
                                                          </div>
                                                        </>
                                                      )}

                                                      {isPromotedToProd && (
                                                        <div className="pt-2 border-t border-emerald-500/30 space-y-2">
                                                          <NobleNumberControl
                                                            label="Umsatz-Faktor als Geselle / Fachkraft (Standard 2,5×)"
                                                            hint="Faktor × neues Gesellen-Bruttogehalt wird auf den Gesamtumsatz addiert"
                                                            value={dep?.customUmsatzFaktor ?? 2.5}
                                                            onChange={(v) =>
                                                              setDeparture(ma.id, {
                                                                customUmsatzFaktor: v,
                                                              })
                                                            }
                                                            min={0}
                                                            max={6.0}
                                                            step={0.1}
                                                            decimals={2}
                                                            suffix="×"
                                                            showSlider={false}
                                                          />
                                                          <label className="flex items-center justify-between gap-2 text-xs cursor-pointer">
                                                            <span className="flex items-center gap-2 text-slate-300">
                                                              <input
                                                                type="checkbox"
                                                                checked={
                                                                  dep?.customUmsatzAnrechnen !== false
                                                                }
                                                                onChange={(e) =>
                                                                  setDeparture(ma.id, {
                                                                    customUmsatzAnrechnen:
                                                                      e.target.checked,
                                                                  })
                                                                }
                                                                className="h-4 w-4 accent-[#dcb68a]"
                                                              />
                                                              Gesellen-Umsatz anrechnen
                                                            </span>
                                                            <span className="font-mono font-bold text-emerald-400">
                                                              {dep?.customUmsatzAnrechnen !== false
                                                                ? `+${formatCurrency(effBrutto * (dep?.customUmsatzFaktor ?? 2.5))} brutto`
                                                                : '0 €'}
                                                            </span>
                                                          </label>
                                                        </div>
                                                      )}
                                                    </>
                                                  )}

                                                  {baseScope === 'produktiv' &&
                                                    (isDeparted || hoursReduced) && (
                                                      <div className="pt-2 border-t border-slate-800/80">
                                                        <NobleNumberControl
                                                          label="Auffangquote im Team (wegfallender Umsatz)"
                                                          value={auffang}
                                                          onChange={(v) =>
                                                            setDeparture(ma.id, {
                                                              auffangQuotePercent: v,
                                                            })
                                                          }
                                                          min={0}
                                                          max={100}
                                                          step={5}
                                                          suffix="%"
                                                        />
                                                      </div>
                                                    )}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()
                    )}
                  </div>
                </div>
              )}

              {/* TAB 2: INHABER */}
              {activeTab === 'inhaber' && (
                <div className="space-y-5">
                  <div className="rounded-xl bg-slate-950/70 border border-slate-800 p-3 text-xs text-slate-400 space-y-1">
                    <div>
                      Ist-Basis: <strong className="text-white">{fmtNum(baseline.inhaberProdWochenstunden, 1)} Std./Wo.</strong> am Stuhl ·
                      Chef-Umsatz (Brutto): <strong className="text-white">{formatCurrency(baseline.inhaberEigenumsatzBruttoMonat)}</strong>
                    </div>
                    {baseline.inhaberProdWochenstunden > 0 && (
                      <div className="text-[11px] font-mono text-[#dcb68a]">
                        Neuer Chef-Umsatz bei {fmtNum(inputs.inhaber.bedienzeitWochenstunden, 1)} Std.:{' '}
                        {formatCurrency(
                          baseline.inhaberEigenumsatzBruttoMonat *
                            (inputs.inhaber.bedienzeitWochenstunden / baseline.inhaberProdWochenstunden)
                        )}{' '}
                        brutto
                      </div>
                    )}
                  </div>

                  <NobleNumberControl
                    label="Bedienzeit am Stuhl (Wochenstunden)"
                    hint="Reduzierst du die Stunden, sinkt dein Eigenumsatz proportional"
                    tooltip="Wie viele Stunden pro Woche stehst du als Inhaber künftig selbst produktiv am Stuhl? Weniger Stuhlzeit reduziert deinen eigenen Bedienumsatz und verlagert deinen Unternehmerlohn in den unproduktiven Bereich."
                    value={inputs.inhaber.bedienzeitWochenstunden}
                    onChange={(v) => updateOwnerInput({ bedienzeitWochenstunden: v })}
                    min={0}
                    max={60}
                    step={1}
                    suffix="Std"
                  />

                  <NobleNumberControl
                    label="Team-Auffangquote für Chef-Umsatz"
                    hint="0 % = Umsatz fällt weg · 100 % = Team fängt alles auf"
                    tooltip="Wenn du deine Stunden am Stuhl reduzierst: Wie viel Prozent deines freiwerdenden Kundenumsatzes kann dein bestehendes Team übernehmen?"
                    value={inputs.inhaber.verlagerungsQuotePercent}
                    onChange={(v) => updateOwnerInput({ verlagerungsQuotePercent: v })}
                    min={0}
                    max={100}
                    step={5}
                    suffix="%"
                  />

                  <NobleNumberControl
                    label="Kalkulatorischer Unternehmerlohn (pro Monat)"
                    hint="Monatlicher Netto-Bedarf inkl. privater Absicherung"
                    tooltip="Dein geplanter monatlicher Privatbedarf (Entnahme + private Kranken-/Altersvorsorge). Die Einkommensteuer darauf wird automatisch mitberechnet."
                    value={inputs.inhaber.kalkUnternehmerlohnMonat}
                    onChange={(v) => updateOwnerInput({ kalkUnternehmerlohnMonat: v })}
                    min={0}
                    max={25000}
                    step={100}
                    suffix={currencyCode}
                  />
                </div>
              )}

              {/* TAB 3: KOSTENSTRUKTUR, MATERIAL, INVESTITIONEN & NEUE DIENSTLEISTUNGEN (1:1 WIE PLANUNGSANALYSE) */}
              {activeTab === 'kosten' && (
                <div className="space-y-6">
                  {/* A) MATERIALKOSTEN / WARENEINSATZ */}
                  <div className="space-y-3 border-b border-slate-800 pb-5">
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-wider text-[#dcb68a]">
                        1. Materialkosten (Wareneinsatz)
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Aktueller IST-Wert aus BWA:{' '}
                        <strong className="text-white">
                          {formatCurrency(baseline.materialkostenMonatNetto)} / Monat
                        </strong>{' '}
                        ({fmtPct(baseline.materialkostenfaktor * 100, 1)})
                      </p>
                    </div>

                    <NobleNumberControl
                      label="Geplanter Ziel-Materialeinsatz (Monat Netto)"
                      hint="Erhöhen oder senken (z. B. neue Pflegeserie / Produktwechsel)"
                      tooltip="Trage deinen geplanten monatlichen Gesamt-Wareneinsatz ein (oder nutze +/−). Die Differenz zum IST-Wert fließt sofort in den neuen Materialfaktor ein."
                      value={
                        Math.round(
                          baseline.materialkostenMonatNetto +
                            (inputs.investitionen.zusatzMaterialMonatNetto ?? 0)
                        )
                      }
                      onChange={(zielVal) =>
                        setZusatzMaterialMonatNetto(
                          Math.round(zielVal - baseline.materialkostenMonatNetto)
                        )
                      }
                      min={0}
                      max={25000}
                      step={50}
                      suffix={currencyCode}
                    />

                    {Math.abs(inputs.investitionen.zusatzMaterialMonatNetto ?? 0) >= 1 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs">
                          <span
                            className={`font-semibold ${
                              (inputs.investitionen.zusatzMaterialMonatNetto ?? 0) > 0
                                ? 'text-rose-400'
                                : 'text-emerald-400'
                            }`}
                          >
                            {(inputs.investitionen.zusatzMaterialMonatNetto ?? 0) > 0
                              ? 'Erhöhung: +'
                              : 'Senkung: '}
                            {formatCurrency(inputs.investitionen.zusatzMaterialMonatNetto ?? 0)} / Monat
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setZusatzMaterialMonatNetto(0);
                              setMaterialNotiz('');
                            }}
                            className="text-xs font-bold text-[#dcb68a] hover:underline"
                          >
                            Zurück auf IST ({formatCurrency(baseline.materialkostenMonatNetto)})
                          </button>
                        </div>
                        <input
                          type="text"
                          value={inputs.investitionen.materialNotiz || ''}
                          onChange={(e) => setMaterialNotiz(e.target.value)}
                          placeholder="Notiz / Grund (z. B. Neue Pflegeserie)"
                          className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-white outline-none focus:border-[#dcb68a]"
                        />
                      </div>
                    )}
                  </div>

                  {/* B) GEMEINKOSTEN DETAIL-ANPASSUNG (AUS DER KOSTENSTRUKTUR / BWA) */}
                  <div className="space-y-3 border-b border-slate-800 pb-5">
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-wider text-[#dcb68a]">
                        2. Gemeinkosten Detail-Anpassung (BWA)
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Wähle eine Kostenart aus deiner Kostenstruktur oder plane eine neue Kostenart ein. Mit{' '}
                        <strong className="text-[#dcb68a]">+ Übernehmen</strong> speicherst du sie in der Liste und kannst die nächste wählen.
                      </p>
                    </div>

                    <select
                      value={gkKey}
                      onChange={(e) => {
                        const selected = e.target.value;
                        setGkKey(selected);
                        if (!selected) {
                          setGkNeuName('');
                          setGkZielwertMonat(0);
                          setGkNotiz('');
                          return;
                        }
                        if (selected === '__neu__') {
                          setGkNeuName('');
                          setGkZielwertMonat(100);
                          setGkNotiz('');
                          return;
                        }
                        const existing = bwaAnpassungenList.find((b) => b.bwaKey === selected);
                        if (existing) {
                          setGkNeuName(existing.label);
                          setGkZielwertMonat(existing.zielMonatNetto);
                          setGkNotiz(existing.notiz || '');
                        } else {
                          const opt = bwaOptions.find((o) => o.bwaKey === selected);
                          const ist = resolveBwaIstMonat(selected);
                          setGkNeuName(opt?.label || selected);
                          setGkZielwertMonat(ist);
                          setGkNotiz('');
                        }
                      }}
                      data-pe-tooltip="Wähle einen BWA-Posten aus deiner Kostenstruktur (z. B. Raumkosten, Werbekosten, KFZ) oder lege mit ‚+ Neue Kostenart‘ einen zusätzlichen Posten an."
                      className="w-full rounded-xl border border-slate-700 bg-slate-950 px-3 py-2.5 text-xs font-semibold text-white outline-none focus:border-[#dcb68a]"
                    >
                      <option value="">BWA-Kostenart wählen …</option>
                      <option value="__neu__">+ Neue Kostenart anlegen</option>
                      {bwaOptions.map((opt) => {
                        const isEdited = bwaAnpassungenList.some((b) => b.bwaKey === opt.bwaKey);
                        return (
                          <option key={opt.bwaKey} value={opt.bwaKey}>
                            {opt.displayLabel}
                            {isEdited ? ' (angepasst)' : ''}
                          </option>
                        );
                      })}
                    </select>

                    {gkKey && (
                      <div className="rounded-xl border border-slate-800 bg-slate-950/80 p-3.5 space-y-3">
                        {(gkKey === '__neu__' || gkKey.startsWith('custom_')) && (
                          <input
                            type="text"
                            value={gkNeuName}
                            onChange={(e) => setGkNeuName(e.target.value)}
                            placeholder="Bezeichnung der neuen Kostenart (z. B. Software / Marketing)"
                            className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-white outline-none focus:border-[#dcb68a]"
                          />
                        )}

                        {gkKey !== '__neu__' && !gkKey.startsWith('custom_') && (
                          <div className="flex items-center justify-between text-xs text-slate-400">
                            <span>Aktueller IST-Wert:</span>
                            <strong className="font-mono text-[#dcb68a]">
                              {formatCurrency(resolveBwaIstMonat(gkKey))} / Monat
                            </strong>
                          </div>
                        )}

                        <NobleNumberControl
                          label="Neuer Zielwert (Monat Netto)"
                          value={gkZielwertMonat}
                          onChange={(v) => setGkZielwertMonat(v)}
                          min={0}
                          max={25000}
                          step={25}
                          suffix={currencyCode}
                          showSlider={false}
                        />

                        <input
                          type="text"
                          value={gkNotiz}
                          onChange={(e) => setGkNotiz(e.target.value)}
                          placeholder="Notiz / Zweck (optional)"
                          className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-white outline-none focus:border-[#dcb68a]"
                        />

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const isCustom = gkKey === '__neu__' || gkKey.startsWith('custom_');
                              const finalKey =
                                gkKey === '__neu__'
                                  ? `custom_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`
                                  : gkKey;
                              const opt = bwaOptions.find((o) => o.bwaKey === gkKey);
                              const istVal = isCustom ? 0 : resolveBwaIstMonat(gkKey);
                              const finalLabel = isCustom
                                ? gkNeuName.trim() || 'Neue Kostenart'
                                : opt?.label || gkNeuName || gkKey;

                              upsertBwaAdjustment({
                                bwaKey: finalKey,
                                label: finalLabel,
                                istMonatNetto: istVal,
                                zielMonatNetto: gkZielwertMonat,
                                notiz: gkNotiz.trim() || undefined,
                                aktiv: true,
                              });
                              setGkKey('');
                              setGkNeuName('');
                              setGkZielwertMonat(0);
                              setGkNotiz('');
                            }}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#dcb68a]/40 bg-[#dcb68a]/15 hover:bg-[#dcb68a]/25 px-3 py-2 text-xs font-black uppercase tracking-wider text-[#dcb68a]"
                          >
                            <Plus size={14} /> Übernehmen &amp; nächste wählen
                          </button>
                        </div>
                      </div>
                    )}

                    {bwaAnpassungenList.length === 0 ? (
                      <p className="text-xs text-slate-500">
                        Keine BWA-Kostenarten angepasst.
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {bwaAnpassungenList.map((item) => {
                          const delta = item.zielMonatNetto - item.istMonatNetto;
                          return (
                            <li
                              key={item.id}
                              onClick={() => {
                                setGkKey(item.bwaKey);
                                setGkNeuName(item.label);
                                setGkZielwertMonat(item.zielMonatNetto);
                                setGkNotiz(item.notiz || '');
                              }}
                              className="flex items-center justify-between gap-2 rounded-xl border border-slate-800 bg-slate-950/80 px-3 py-2.5 cursor-pointer hover:border-[#dcb68a]/40"
                            >
                              <div className="min-w-0 flex-1">
                                <span className="text-xs font-bold text-white block truncate">
                                  {item.label}
                                </span>
                                <span className="text-[11px] font-mono text-slate-400">
                                  IST {formatCurrency(item.istMonatNetto)} →{' '}
                                  <strong className="text-[#dcb68a]">
                                    {formatCurrency(item.zielMonatNetto)}
                                  </strong>{' '}
                                  ({delta >= 0 ? `+${formatCurrency(delta)}` : formatCurrency(delta)} / Mon.)
                                </span>
                                {item.notiz && (
                                  <span className="block text-[10px] text-slate-500 truncate">
                                    Notiz: {item.notiz}
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeBwaAdjustment(item.id);
                                  if (gkKey === item.bwaKey) setGkKey('');
                                }}
                                className="text-slate-500 hover:text-rose-400 p-1 shrink-0"
                                title="Anpassung löschen / auf IST zurücksetzen"
                              >
                                <Trash2 size={14} />
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  {/* C) GEPLANTE INVESTITIONEN (INKL. UMSCHALTER MONATLICH VS. GESAMTKOSTEN) */}
                  <div className="space-y-3 border-b border-slate-800 pb-5">
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-wider text-[#dcb68a]">
                        3. Geplante Investitionen
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Leasing, Seminar, Weiterbildung, Einrichtung oder Geräte (netto)
                      </p>
                    </div>

                    <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3.5 space-y-3">
                      <input
                        type="text"
                        value={invNameDraft}
                        onChange={(e) => setInvNameDraft(e.target.value)}
                        placeholder="Bezeichnung (z. B. Seminar / Neue Waschplätze / Umbau)"
                        className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-white outline-none focus:border-[#dcb68a]"
                      />

                      {/* Umschalter: Monatliche Rate vs. Gesamtkosten (Jahr) */}
                      <div className="space-y-1">
                        <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Betrag eingeben als:
                        </span>
                        <div className="grid grid-cols-2 gap-1.5 rounded-lg border border-slate-800 bg-slate-900 p-1">
                          <button
                            type="button"
                            onClick={() => {
                              if (invInputModus === 'gesamt') {
                                setInvRateDraft(
                                  Math.round(invRateDraft / (zeitraumMonate > 0 ? zeitraumMonate : 12))
                                );
                                setInvInputModus('monat');
                              }
                            }}
                            data-pe-tooltip="Trage direkt die monatliche Rate ein (z. B. 250 € Leasing pro Monat)."
                            className={`rounded-md py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                              invInputModus === 'monat'
                                ? 'bg-[#dcb68a] text-slate-950'
                                : 'text-slate-400 hover:text-white'
                            }`}
                          >
                            Monatliche Kosten
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (invInputModus === 'monat') {
                                setInvRateDraft(
                                  Math.round(invRateDraft * (zeitraumMonate > 0 ? zeitraumMonate : 12))
                                );
                                setInvInputModus('gesamt');
                              }
                            }}
                            data-pe-tooltip="Trage einen Gesamt-/Jahresbetrag ein (z. B. 2.500 € für ein Seminar). Wird automatisch auf die monatliche Belastung heruntergerechnet."
                            className={`rounded-md py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                              invInputModus === 'gesamt'
                                ? 'bg-[#dcb68a] text-slate-950'
                                : 'text-slate-400 hover:text-white'
                            }`}
                          >
                            Gesamtkosten (Jahr)
                          </button>
                        </div>
                      </div>

                      <NobleNumberControl
                        label={
                          invInputModus === 'gesamt'
                            ? `Gesamtkosten / Jahresbetrag (Netto)`
                            : `Monatliche Kosten / Rate (Netto)`
                        }
                        value={invRateDraft}
                        onChange={(v) => setInvRateDraft(v)}
                        min={0}
                        max={invInputModus === 'gesamt' ? 120000 : 10000}
                        step={invInputModus === 'gesamt' ? 100 : 25}
                        suffix={currencyCode}
                        showSlider={false}
                      />

                      <div className="text-[11px] font-mono text-slate-400 flex items-center justify-between px-1">
                        {invInputModus === 'gesamt' ? (
                          <>
                            <span>Monatliche Belastung (÷ {zeitraumMonate} Mon.):</span>
                            <strong className="text-[#dcb68a]">
                              = {formatCurrency(Math.round(invRateDraft / (zeitraumMonate > 0 ? zeitraumMonate : 12)))} / Monat
                            </strong>
                          </>
                        ) : (
                          <>
                            <span>Auf das Jahr ({zeitraumMonate} Mon.):</span>
                            <strong className="text-slate-300">
                              = {formatCurrency(Math.round(invRateDraft * (zeitraumMonate > 0 ? zeitraumMonate : 12)))} / Jahr
                            </strong>
                          </>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          if (invRateDraft <= 0) return;
                          const monatsrateNetto =
                            invInputModus === 'gesamt'
                              ? Math.round(invRateDraft / (zeitraumMonate > 0 ? zeitraumMonate : 12))
                              : Math.round(invRateDraft);
                          if (monatsrateNetto <= 0) return;
                          addInvestmentItem({
                            label:
                              invNameDraft.trim() ||
                              `Investition ${inputs.investitionen.einzelInvestitionen.length + 1}`,
                            monatsrateNetto,
                            aktiv: true,
                          });
                          setInvNameDraft('');
                          setInvRateDraft(invInputModus === 'gesamt' ? 2500 : 250);
                        }}
                        className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#dcb68a]/40 bg-[#dcb68a]/15 hover:bg-[#dcb68a]/25 px-3 py-2 text-xs font-black uppercase tracking-wider text-[#dcb68a]"
                      >
                        <Plus size={14} /> Investition hinzufügen
                      </button>
                    </div>

                    {inputs.investitionen.einzelInvestitionen.length === 0 ? (
                      <p className="text-xs text-slate-500">
                        Noch keine Investitionen hinterlegt.
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {inputs.investitionen.einzelInvestitionen.map((inv) => (
                          <li
                            key={inv.id}
                            className="flex items-center justify-between gap-2 rounded-xl bg-slate-950/80 border border-slate-800 px-3 py-2.5"
                          >
                            <input
                              type="checkbox"
                              checked={inv.aktiv}
                              onChange={(e) =>
                                updateInvestmentItem(inv.id, { aktiv: e.target.checked })
                              }
                              className="h-4 w-4 accent-[#dcb68a]"
                            />
                            <div className="min-w-0 flex-1">
                              <input
                                type="text"
                                value={inv.label}
                                onChange={(e) =>
                                  updateInvestmentItem(inv.id, { label: e.target.value })
                                }
                                className="bg-transparent text-xs font-semibold text-white outline-none w-full truncate"
                              />
                              <span className="text-[10px] font-mono text-slate-400 block">
                                Gesamt ({zeitraumMonate} Mon.):{' '}
                                {formatCurrency(inv.monatsrateNetto * zeitraumMonate)}
                              </span>
                            </div>
                            <span className="font-mono text-xs font-bold text-[#dcb68a] shrink-0">
                              {formatCurrency(inv.monatsrateNetto)} / Mon.
                            </span>
                            <button
                              type="button"
                              onClick={() => removeInvestmentItem(inv.id)}
                              className="text-slate-500 hover:text-rose-400 p-1 shrink-0"
                              title="Investition löschen"
                            >
                              <Trash2 size={14} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* D) NEUE DIENSTLEISTUNGEN (MEHRUMSATZ + MATERIALVERBRAUCH) */}
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-wider text-[#dcb68a]">
                        4. Neue Dienstleistungen (Mehrumsatz &amp; Material)
                      </h3>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Plane neue Dienstleistungen (z. B. Keratin, Head Spa, Extensions) inkl. monatlichem Mehrumsatz und zugehörigem Materialeinsatz ein.
                      </p>
                    </div>

                    <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3.5 space-y-3">
                      <input
                        type="text"
                        value={dlNameDraft}
                        onChange={(e) => setDlNameDraft(e.target.value)}
                        placeholder="Bezeichnung (z. B. Keratin-Behandlung / Head Spa)"
                        className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-white outline-none focus:border-[#dcb68a]"
                      />
                      <NobleNumberControl
                        label="Geplanter Mehrumsatz pro Monat (Brutto)"
                        tooltip="Wie viel zusätzlichen Kassenumsatz (Brutto) pro Monat erwartest du durch diese neue Dienstleistung?"
                        value={dlUmsatzDraft}
                        onChange={(v) => setDlUmsatzDraft(v)}
                        min={0}
                        max={20000}
                        step={50}
                        suffix={currencyCode}
                        showSlider={false}
                      />
                      <NobleNumberControl
                        label="Zusätzlicher Materialverbrauch pro Monat (Netto)"
                        tooltip="Wie viel zusätzlicher Wareneinsatz / Materialverbrauch fällt monatlich für diese neue Dienstleistung an?"
                        value={dlMaterialDraft}
                        onChange={(v) => setDlMaterialDraft(v)}
                        min={0}
                        max={5000}
                        step={10}
                        suffix={currencyCode}
                        showSlider={false}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (dlUmsatzDraft <= 0 && dlMaterialDraft <= 0) return;
                          addNewServiceItem({
                            label:
                              dlNameDraft.trim() ||
                              `Neue Dienstleistung ${neueDienstleistungenList.length + 1}`,
                            zusatzUmsatzMonatBrutto: dlUmsatzDraft,
                            zusatzMaterialMonatNetto: dlMaterialDraft,
                            aktiv: true,
                          });
                          setDlNameDraft('');
                          setDlUmsatzDraft(500);
                          setDlMaterialDraft(50);
                        }}
                        className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#dcb68a]/40 bg-[#dcb68a]/15 hover:bg-[#dcb68a]/25 px-3 py-2 text-xs font-black uppercase tracking-wider text-[#dcb68a]"
                      >
                        <Plus size={14} /> Dienstleistung hinzufügen
                      </button>
                    </div>

                    {neueDienstleistungenList.length === 0 ? (
                      <p className="text-xs text-slate-500">
                        Noch keine neuen Dienstleistungen hinterlegt.
                      </p>
                    ) : (
                      <ul className="space-y-2.5">
                        {neueDienstleistungenList.map((dl) => (
                          <li
                            key={dl.id}
                            className="rounded-xl bg-slate-950/80 border border-slate-800 p-3 space-y-2"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <label className="flex items-center gap-2 min-w-0 flex-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={dl.aktiv}
                                  onChange={(e) =>
                                    updateNewServiceItem(dl.id, { aktiv: e.target.checked })
                                  }
                                  className="h-4 w-4 accent-[#dcb68a]"
                                />
                                <input
                                  type="text"
                                  value={dl.label}
                                  onChange={(e) =>
                                    updateNewServiceItem(dl.id, { label: e.target.value })
                                  }
                                  className="bg-transparent text-xs font-bold text-white outline-none flex-1 min-w-0 truncate"
                                />
                              </label>
                              <button
                                type="button"
                                onClick={() => removeNewServiceItem(dl.id)}
                                className="text-slate-500 hover:text-rose-400 p-1 shrink-0"
                                title="Dienstleistung löschen"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                            <div className="flex items-center justify-between text-[11px] font-mono text-slate-300 pl-6">
                              <span className="text-emerald-400">
                                +{formatCurrency(dl.zusatzUmsatzMonatBrutto)} Umsatz (Brutto)
                              </span>
                              <span className="text-[#dcb68a]">
                                +{formatCurrency(dl.zusatzMaterialMonatNetto)} Material (Netto)
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}

              {/* TAB 4: PREISANPASSUNG */}
              {activeTab === 'preis' && (
                <div className="space-y-5">
                  <NobleNumberControl
                    label="Geplante Preiserhöhung im Salon"
                    hint="Prozentuale Preisanpassung über den gesamten Salon"
                    tooltip="Um wie viel Prozent möchtest du deine Dienstleistungspreise im Salon durchschnittlich anheben? Du siehst rechts sofort, wie sich deine Stundensätze, dein Überschuss und deine Deckungslücke verändern."
                    value={inputs.preisanpassung.geplantePreiserhoehungPercent}
                    onChange={(v) =>
                      updatePriceAdjustment({ geplantePreiserhoehungPercent: v })
                    }
                    min={-10}
                    max={40}
                    step={0.5}
                    decimals={1}
                    suffix="%"
                  />

                  <div className="rounded-xl border border-slate-800 bg-slate-950/70 p-3.5 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-400">
                        {inputs.preisanpassung.kundenSchwundPercent !== null
                          ? 'Manueller Kundenverlust:'
                          : 'Automatischer Kundenverlust (20 % Preis = 5 % Verlust):'}
                      </span>
                      <span className="font-mono font-bold text-[#dcb68a]">
                        {fmtPct(outputs.preiskonsequenz.effektiverKundenSchwundPercent ?? 0, 1)}
                      </span>
                    </div>
                    <label
                      className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer pt-1"
                      data-pe-tooltip="Standardmäßig rechnet das System automatisch mit der Praxis-Regel: 20 % Preisanpassung = 5 % Kundenverlust. Setze den Haken, wenn du die Quote manuell überschreiben möchtest."
                    >
                      <input
                        type="checkbox"
                        checked={inputs.preisanpassung.kundenSchwundPercent !== null}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const autoVal = Math.max(
                              0,
                              inputs.preisanpassung.geplantePreiserhoehungPercent / 4
                            );
                            updatePriceAdjustment({ kundenSchwundPercent: Number(autoVal.toFixed(1)) });
                          } else {
                            updatePriceAdjustment({ kundenSchwundPercent: null });
                          }
                        }}
                        className="h-4 w-4 accent-[#dcb68a]"
                      />
                      Kundenverlust manuell abweichend einstellen
                    </label>
                  </div>

                  {inputs.preisanpassung.kundenSchwundPercent !== null && (
                    <NobleNumberControl
                      label="Manueller Kundenverlust"
                      hint="Abweichende Kundenverlust-Quote in %"
                      tooltip="Manuelle Überschreibung der automatischen 4:1-Regel."
                      value={inputs.preisanpassung.kundenSchwundPercent ?? 0}
                      onChange={(v) =>
                        updatePriceAdjustment({ kundenSchwundPercent: v })
                      }
                      min={0}
                      max={30}
                      step={0.5}
                      decimals={1}
                      suffix="%"
                    />
                  )}
                </div>
              )}
            </div>
          </aside>

          {/* ==================================================================
              3. KERN-AUSWERTUNG (RECHTS — GROSS, KLAR, LESBAR WIE BWA-REPORT)
              ================================================================== */}
          <div className="min-w-0 space-y-6">
            <section className="rounded-2xl border border-slate-800 bg-slate-900/95 p-5 md:p-7 shadow-xl space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border-b border-slate-800 pb-4">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#dcb68a]">
                    Szenario-Vergleich · Vorher / Nachher
                  </p>
                  <h2 className="text-xl md:text-2xl font-black text-white">
                    Wirtschaftliche Gesamtauswertung (Monat)
                  </h2>
                </div>
                <div className="text-xs font-mono text-slate-400">
                  MwSt: {fmtPct(globalSettings.mwstSatzPercent, 1)} · Rücklagenziel:{' '}
                  {fmtPct(globalSettings.ruecklagenSatzPercent, 0)}
                  {outputs.isEffectiveSolo ? ' · Solo' : ' · Team'}
                </div>
              </div>

              {outputs.modusWechsel && (
                <div className="rounded-xl border border-amber-500/50 bg-amber-950/40 px-4 py-3 text-sm text-amber-100 leading-relaxed">
                  {outputs.modusWechsel === 'solo-zu-team' ? (
                    <>
                      <strong className="font-black uppercase tracking-wide text-amber-300">Wechsel zu Team.</strong>{' '}
                      Mit dem ersten Mitarbeiter gelten Lohnfaktor, Kostendeckungsfaktor und Lohnnebenkosten.
                      Der Unternehmerlohn teilt sich wieder in produktiv und unproduktiv.
                    </>
                  ) : (
                    <>
                      <strong className="font-black uppercase tracking-wide text-amber-300">Wechsel zu Solo.</strong>{' '}
                      Ohne Mitarbeiter entfallen Lohnfaktor, Kostendeckungsfaktor und Lohnnebenkosten.
                      Der Unternehmerlohn ist zu 100 % produktiv (Bedarf ÷ Bedienstunden).
                    </>
                  )}
                </div>
              )}

              {/* A) UMSATZZIELE & PREISKONSEQUENZ (PRIMÄR IN BRUTTO!) */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div
                  className="rounded-xl bg-slate-950/90 border border-slate-800 p-4 space-y-1.5"
                  data-pe-tooltip="Mindest-Umsatz (Brutto), den der Salon im Monat erreichen muss, um alle Kosten inkl. Lohnfortzahlung und Unternehmerlohn auf 0 € zu decken (ohne zusätzliche Gewinnrücklage)."
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Umsatz Break-Even (ohne Rücklage)
                  </span>
                  <div className="text-xl font-mono font-black text-white">
                    {formatCurrency(outputs.umsatzzieleMonat.inklFortzahlungOhneRuecklageBrutto)}{' '}
                    <span className="text-xs font-sans font-normal text-slate-400">Brutto</span>
                  </div>
                  <div className="text-xs font-mono text-slate-500">
                    ({formatCurrency(outputs.umsatzzieleMonat.inklFortzahlungOhneRuecklageNetto)} Netto)
                  </div>
                  {(outputs.umsatzzieleMonat.foerderzuschussMonat || 0) > 0 && (
                    <div className="pt-2 mt-1 border-t border-slate-800 text-[11px] leading-snug text-emerald-300">
                      Staatlicher Förderzuschuss (neutral): −
                      {formatCurrency(outputs.umsatzzieleMonat.foerderzuschussMonat)}
                      <span className="block text-slate-400">
                        Break-even nach Förderung:{' '}
                        {formatCurrency(outputs.umsatzzieleMonat.breakEvenNachFoerderungBrutto)} Brutto.
                        Lohnfaktor, KDF und Teamlohn bleiben auf dem vollen Brutto.
                      </span>
                    </div>
                  )}
                </div>

                <div
                  className="rounded-xl bg-[#dcb68a]/10 border border-[#dcb68a]/50 p-4 space-y-1.5"
                  data-pe-tooltip="Empfohlener Ziel-Umsatz (Brutto) pro Monat inklusive deiner hinterlegten Sicherheits- und Gewinnrücklage."
                >
                  <span className="text-[10px] font-black uppercase tracking-wider text-[#dcb68a] block">
                    Ziel-Umsatz MIT {fmtNum(outputs.umsatzzieleMonat.ruecklagenSatzPercent, 0)} % Rücklage
                  </span>
                  <div className="text-xl font-mono font-black text-[#dcb68a]">
                    {formatCurrency(outputs.umsatzzieleMonat.mitRuecklageBrutto)}{' '}
                    <span className="text-xs font-sans font-normal text-slate-200">Brutto</span>
                  </div>
                  <div className="text-xs font-mono text-slate-400">
                    ({formatCurrency(outputs.umsatzzieleMonat.mitRuecklageNetto)} Netto)
                  </div>
                </div>

                <div
                  className="rounded-xl bg-slate-950/90 border border-slate-800 p-4 space-y-1.5"
                  data-pe-tooltip="Zeigt dir sofort, um wie viel Prozent du deine Preise im Salon durchschnittlich anheben müsstest — inklusive des kalkulierten Kundenverlusts (20 % Preisanpassung = 5 % Kundenverlust)."
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Erforderliche Preisanpassung
                  </span>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs text-slate-400">Kostendeckend:</span>
                    <span className="text-base font-mono font-bold text-white">
                      {outputs.preiskonsequenz.erforderlicheAnpassungKostendeckendPercent > 0 ? '+' : ''}
                      {fmtPct(outputs.preiskonsequenz.erforderlicheAnpassungKostendeckendPercent, 1)}
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-xs text-[#dcb68a] font-bold">
                      Inkl. {fmtNum(globalSettings.ruecklagenSatzPercent, 0)} % Rückl.:
                    </span>
                    <span className="text-lg font-mono font-black text-[#dcb68a]">
                      {outputs.preiskonsequenz.erforderlicheAnpassungGewinnstabilPercent > 0 ? '+' : ''}
                      {fmtPct(outputs.preiskonsequenz.erforderlicheAnpassungGewinnstabilPercent, 1)}
                    </span>
                  </div>
                  {(outputs.preiskonsequenz.kalkulierterKundenverlustZielPercent ?? 0) > 0 && (
                    <div className="text-[11px] font-mono text-slate-400 pt-0.5">
                      (inkl. {fmtPct(outputs.preiskonsequenz.kalkulierterKundenverlustZielPercent ?? 0, 1)} Kundenverlust eingerechnet)
                    </div>
                  )}
                </div>
              </div>

              {/* 2. DIE 4 FAKTOREN AUS DER KURZAUSWERTUNG (GANZ OBEN ÜBER RENTABILITÄT & MULTIPLIKATOR) */}
              <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                <div
                  className="bg-slate-950/90 p-3.5 rounded-xl border-l-4 border-l-cyan-500 border-y border-r border-slate-800"
                  data-pe-tooltip="Gemeinkostenfaktor (% vom Netto-Umsatz). Steigt bei Zusatzkosten/Investitionen und sinkt virtuell, wenn der Umsatz durch Preiserhöhungen oder mehr Personal steigt."
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Gemeinkostenfaktor
                  </span>
                  <div className="flex items-baseline justify-between gap-2 mt-1">
                    <span className="text-lg font-mono font-black text-white">
                      {fmtPct(outputs.vergleich.gemeinkostenfaktorPercent.nachher, 1)}
                    </span>
                    <span className="text-[11px] font-mono text-slate-500">
                      Vorher: {fmtPct(outputs.vergleich.gemeinkostenfaktorPercent.vorher, 1)}
                    </span>
                  </div>
                </div>

                <div
                  className="bg-slate-950/90 p-3.5 rounded-xl border-l-4 border-l-teal-500 border-y border-r border-slate-800"
                  data-pe-tooltip="Materialkostenfaktor (% vom Netto-Umsatz). Reagiert auf zusätzlichen Materialeinsatz (z. B. Pflegeserie), Personalveränderungen und virtuell auf Preisanpassungen."
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Materialkostenfaktor
                  </span>
                  <div className="flex items-baseline justify-between gap-2 mt-1">
                    <span className="text-lg font-mono font-black text-white">
                      {fmtPct(outputs.vergleich.materialkostenfaktorPercent.nachher, 1)}
                    </span>
                    <span className="text-[11px] font-mono text-slate-500">
                      Vorher: {fmtPct(outputs.vergleich.materialkostenfaktorPercent.vorher, 1)}
                    </span>
                  </div>
                </div>

                <div
                  className="bg-slate-950/90 p-3.5 rounded-xl border-l-4 border-l-slate-500 border-y border-r border-slate-800"
                  data-pe-tooltip="Gerätekostenfaktor (% vom Netto-Umsatz)."
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Gerätefaktor
                  </span>
                  <div className="flex items-baseline justify-between gap-2 mt-1">
                    <span className="text-lg font-mono font-black text-white">
                      {fmtPct(outputs.vergleich.geraetekostenfaktorPercent.nachher, 1)}
                    </span>
                    <span className="text-[11px] font-mono text-slate-500">
                      Vorher: {fmtPct(outputs.vergleich.geraetekostenfaktorPercent.vorher, 1)}
                    </span>
                  </div>
                </div>

                <div
                  className="bg-slate-950/90 p-3.5 rounded-xl border-l-4 border-l-indigo-500 border-y border-r border-slate-800"
                  data-pe-tooltip="Produktiver Lohnkostenfaktor inkl. Inhaber (% vom Netto-Umsatz). Verändert sich durch Einstellungen, Stundenänderungen, Abgänge und virtuell durch Preiserhöhungen."
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                    Prod. Lohnkostenfaktor
                  </span>
                  <div className="flex items-baseline justify-between gap-2 mt-1">
                    <span className="text-lg font-mono font-black text-white">
                      {fmtPct(outputs.vergleich.prodLohnkostenfaktorPercent.nachher, 1)}
                    </span>
                    <span className="text-[11px] font-mono text-slate-500">
                      Vorher: {fmtPct(outputs.vergleich.prodLohnkostenfaktorPercent.vorher, 1)}
                    </span>
                  </div>
                </div>
              </div>

              {/* 3. MULTIPLIKATOREN — nur Team. Solo: Live-Form ohne Lohnfaktor und ohne KDF. */}
              {outputs.isEffectiveSolo ? (
                (() => {
                  const soloEntnahme = Number(inputs.inhaber.kalkUnternehmerlohnMonat) || 0;
                  const soloWochenStd = Number(inputs.inhaber.bedienzeitWochenstunden) || 0;
                  const soloMonatsStd = soloWochenStd * (Number(globalSettings.wochensatz) || 4.33);
                  const soloStdLohn = Number(outputs.neuerInhaberProdStundenlohn) || 0;
                  const urlPct = Number(globalSettings.urlaubsausgleichPercent) || 0;
                  const soloTeamLohn = soloStdLohn * (1 + urlPct / 100);
                  return (
                    <div className="rounded-xl border border-pink-500/40 bg-pink-950/30 p-4 space-y-4">
                      <div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-pink-300 block">
                          Soloselbstständigkeit
                        </span>
                        <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                          Kein Lohnfaktor, kein Kostendeckungsfaktor, keine Lohnnebenkosten.
                          Unternehmerlohn zu 100 % produktiv. Rücklage aus den Globaldaten:{' '}
                          {fmtNum(globalSettings.ruecklagenSatzPercent, 0)} %.
                        </p>
                      </div>
                      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
                        <div className="min-w-0 h-full overflow-hidden rounded-lg bg-slate-950/70 border border-pink-500/20 px-3 py-2.5">
                          <span className="text-[10px] font-bold uppercase tracking-normal text-slate-400 block leading-tight">
                            Gesamtentnahme
                            <br />
                            pro Monat
                          </span>
                          <span className="text-lg font-mono font-black text-white mt-2 block">
                            {formatCurrency(soloEntnahme, 0)}
                          </span>
                        </div>
                        <div className="min-w-0 h-full overflow-hidden rounded-lg bg-slate-950/70 border border-pink-500/20 px-3 py-2.5">
                          <span className="text-[10px] font-bold uppercase tracking-normal text-slate-400 block leading-tight">
                            Bedienzeit
                          </span>
                          <span className="text-lg font-mono font-black text-white mt-2 block">
                            {fmtNum(soloWochenStd, 1)} Std/Wo
                          </span>
                          <span className="text-[11px] font-mono text-slate-400 block">
                            {fmtNum(soloMonatsStd, 1)} Std/Monat
                          </span>
                        </div>
                        <div className="min-w-0 h-full overflow-hidden rounded-lg bg-slate-950/70 border border-pink-500/20 px-3 py-2.5">
                          <span className="text-[10px] font-bold uppercase tracking-normal text-slate-400 block leading-tight">
                            Unternehmer-
                            <br />
                            Stundenlohn
                          </span>
                          <span className="text-lg font-mono font-black text-pink-200 mt-2 block">
                            {formatCurrency(soloStdLohn, 2)}
                          </span>
                          <span className="text-[11px] font-mono text-slate-400 block leading-tight">
                            Entnahme / Monatsstunden
                          </span>
                        </div>
                        <div className="min-w-0 h-full overflow-hidden rounded-lg bg-pink-500/15 border border-pink-400/40 px-3 py-2.5">
                          <span className="text-[10px] font-bold uppercase tracking-normal text-pink-200 block leading-tight">
                            Teamstundenlohn
                            <br />
                            inkl. Urlaub
                          </span>
                          <span className="text-lg font-mono font-black text-pink-100 mt-2 block">
                            {formatCurrency(soloTeamLohn, 2)}
                          </span>
                          <span className="text-[11px] font-mono text-pink-200/80 block leading-tight">
                            + {fmtNum(urlPct, 0)} % Urlaubsausgleich, ohne Lohnnebenkosten
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })()
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className="rounded-xl bg-slate-950/80 border border-slate-800 p-4 flex items-center justify-between gap-4"
                  data-pe-tooltip="Der neue aktive Lohnfaktor im Szenario (inkl. Lohnfortzahlung und Sicherheitszuschlag, mindestens 3,50). Mit diesem Faktor wird das Bruttogehalt der Mitarbeiter für deren Soll-Umsatz multipliziert."
                >
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">
                      Neuer aktiver Lohnfaktor
                    </span>
                    <span className="text-xs text-slate-500">
                      Vorher: {outputs.baselineIsSolo ? 'entfällt (Solo)' : `${fmtNum(outputs.vergleich.lohnfaktorAktiv.vorher, 2)} · Floor 3,50`}
                    </span>
                  </div>
                  <span className="text-2xl font-mono font-black text-[#dcb68a]">
                    {fmtNum(outputs.neuerLohnfaktorAktiv, 2)}
                  </span>
                </div>

                <div
                  className="rounded-xl bg-slate-950/80 border border-slate-800 p-4 flex items-center justify-between gap-4"
                  data-pe-tooltip="Der neue Kostendeckungsfaktor (KDF, mindestens 2,80). Er gibt an, welchen Faktor ein Mitarbeiter mindestens einspielen muss, damit dein Unternehmerlohn zu 100 % gesichert bleibt."
                >
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">
                      Neuer Kostendeckungsfaktor (KDF)
                    </span>
                    <span className="text-xs text-slate-500">
                      Vorher: {outputs.baselineIsSolo ? 'entfällt (Solo)' : fmtNum(outputs.vergleich.kostendeckungsfaktor.vorher, 2)}
                    </span>
                  </div>
                  <span className="text-2xl font-mono font-black text-white">
                    {fmtNum(outputs.neuerKostendeckungsfaktor, 2)}
                  </span>
                </div>
              </div>
              )}

              {/* 4. KALKULATORISCHE RENTABILITÄT & ÜBERSCHUSS */}
              {(() => {
                const ueberschussNeu = outputs.vergleich.ueberschussMonatNetto.nachher;
                const ueberschussPctNeu = outputs.vergleich.ueberschussPercent.nachher;
                const rentabilitaetPctNeu = outputs.vergleich.rentabilitaetPercent.nachher;
                const rentabilitaetPctVorher = outputs.vergleich.rentabilitaetPercent.vorher;
                const gehaltSzenario = inputs.inhaber.kalkUnternehmerlohnMonat || 0;
                const targetPct = globalSettings.ruecklagenSatzPercent || 10;

                // Ziel erreicht erst ab targetPct (mit minimaler Rundungstoleranz -0.05)
                const isTargetReached = ueberschussPctNeu >= targetPct - 0.05;

                const ueberschussStatus = isTargetReached
                  ? {
                      text: 'Ziel erreicht',
                      color: 'text-emerald-400',
                      bgColor: 'bg-emerald-950/25',
                      borderColor: 'border-emerald-500/50',
                    }
                  : ueberschussPctNeu > 0
                    ? {
                        text: 'Kostendeckend',
                        color: 'text-amber-400',
                        bgColor: 'bg-amber-950/25',
                        borderColor: 'border-amber-500/50',
                      }
                    : {
                        text: 'Verlust',
                        color: 'text-red-400',
                        bgColor: 'bg-red-950/25',
                        borderColor: 'border-red-500/50',
                      };

                // Rentabilitäts-Bewertung: Koppelt Doppel-Schloss-Matrix mit echten Rentabilitäts-Schwellen:
                // "Gut" erst ab >= 30 % Rentabilität UND erreichtem 10 % Überschussziel!
                // "Exzellent" erst ab >= 35 % Rentabilität UND erreichtem Überschussziel!
                const baseEval = evaluateRentabilitaet(gehaltSzenario, ueberschussPctNeu);
                let finalStatus: 'Kritisch' | 'Grenzwertig' | 'Solide' | 'Gut' | 'Exzellent' =
                  baseEval.status;
                let finalMessage = baseEval.message;

                if (ueberschussPctNeu < 0 || rentabilitaetPctNeu < 20) {
                  finalStatus = 'Kritisch';
                  if (ueberschussPctNeu >= 0) {
                    finalMessage =
                      'Kritische Rentabilität (unter 20 %): Der Gesamtertrag im Verhältnis zum Umsatz ist noch zu niedrig.';
                  }
                } else if (rentabilitaetPctNeu < 25 || ueberschussPctNeu < 5) {
                  if (finalStatus !== 'Kritisch') {
                    finalStatus = 'Grenzwertig';
                    finalMessage =
                      'Grenzwertig: Das Unternehmen arbeitet zwar ohne Verlust, aber Rentabilität (unter 25 %) und Rücklage bieten noch keinen sicheren Puffer.';
                  }
                } else if (rentabilitaetPctNeu < 30 || !isTargetReached) {
                  if (finalStatus === 'Gut' || finalStatus === 'Exzellent') {
                    finalStatus = 'Solide';
                    finalMessage = !isTargetReached
                      ? `Solides Fundament (${fmtPct(rentabilitaetPctNeu, 1)} Rentabilität), aber das Überschuss-Ziel von ${fmtNum(targetPct, 0)} % ist noch nicht ganz erreicht.`
                      : 'Solides Fundament (25–30 % Rentabilität): Gute Basis, für den Status „Gut“ werden mindestens 30 % Rentabilität benötigt.';
                  }
                } else if (rentabilitaetPctNeu < 35) {
                  if (finalStatus === 'Exzellent') {
                    finalStatus = 'Gut';
                    finalMessage =
                      'Gute und gesunde Unternehmensstruktur (ab 30 % Rentabilität bei erreichtem Rücklagenziel).';
                  }
                }

                const rentabilityStatus =
                  finalStatus === 'Kritisch'
                    ? {
                        label: 'KRITISCH',
                        message: finalMessage,
                        color: 'text-red-400',
                        bgColor: 'bg-red-950/25',
                        borderColor: 'border-red-500/50',
                      }
                    : finalStatus === 'Grenzwertig'
                      ? {
                          label: 'GRENZWERTIG',
                          message: finalMessage,
                          color: 'text-amber-400',
                          bgColor: 'bg-amber-950/25',
                          borderColor: 'border-amber-500/50',
                        }
                      : finalStatus === 'Solide'
                        ? {
                            label: 'SOLIDE',
                            message: finalMessage,
                            color: 'text-lime-400',
                            bgColor: 'bg-lime-950/25',
                            borderColor: 'border-lime-500/50',
                          }
                        : finalStatus === 'Gut'
                          ? {
                              label: 'GUT',
                              message: finalMessage,
                              color: 'text-emerald-400',
                              bgColor: 'bg-emerald-950/25',
                              borderColor: 'border-emerald-500/50',
                            }
                          : {
                              label: 'EXZELLENT',
                              message: finalMessage,
                              color: 'text-purple-400',
                              bgColor: 'bg-purple-950/25',
                              borderColor: 'border-purple-500/50',
                            };

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* KALKULATORISCHE RENTABILITÄT (KOMPAKT, OHNE ZEILENUMBRUCH) */}
                    <div
                      className={`rounded-xl px-4 py-3 border ${rentabilityStatus.borderColor} ${rentabilityStatus.bgColor} flex flex-col justify-between transition-all duration-300`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <TrendingUp size={18} className={`shrink-0 ${rentabilityStatus.color}`} />
                          <div className="min-w-0">
                            <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
                              Kalkulatorische Rentabilität
                            </div>
                            <div className="flex flex-wrap items-baseline gap-2">
                              <span className={`text-xl font-black font-mono whitespace-nowrap ${rentabilityStatus.color}`}>
                                {fmtPct(rentabilitaetPctNeu, 1)}
                              </span>
                              <span className="text-[11px] font-mono text-slate-400 whitespace-nowrap">
                                (Vorher: {fmtPct(rentabilitaetPctVorher, 1)})
                              </span>
                            </div>
                          </div>
                        </div>
                        <span className={`font-black text-xs uppercase tracking-widest px-2 py-0.5 rounded bg-black/20 shrink-0 ${rentabilityStatus.color}`}>
                          {rentabilityStatus.label}
                        </span>
                      </div>
                      <div
                        className={`mt-2 pt-2 border-t ${rentabilityStatus.borderColor} text-[11px] leading-snug ${rentabilityStatus.color} opacity-95`}
                      >
                        {rentabilityStatus.message}
                      </div>
                    </div>

                    {/* KALK. ÜBERSCHUSS / VERLUST (KOMPAKT DANEBEN) */}
                    <div
                      className={`rounded-xl px-4 py-3 border ${ueberschussStatus.borderColor} ${ueberschussStatus.bgColor} flex flex-col justify-between transition-all duration-300`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
                            Kalk. Überschuss / Verlust
                          </div>
                          <div className="flex flex-wrap items-baseline gap-2">
                            <span className={`text-xl font-black font-mono whitespace-nowrap ${ueberschussStatus.color}`}>
                              {formatCurrency(ueberschussNeu)}
                            </span>
                            <span className={`text-xs font-bold font-mono whitespace-nowrap ${ueberschussStatus.color} opacity-85`}>
                              ({fmtPct(ueberschussPctNeu, 1)})
                            </span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">
                            Ziel: mind. {fmtNum(targetPct, 0)} %
                          </div>
                          <div className={`font-black tracking-wider uppercase text-xs mt-0.5 ${ueberschussStatus.color}`}>
                            {ueberschussStatus.text}
                          </div>
                        </div>
                      </div>
                      <div className={`mt-2 pt-2 border-t ${ueberschussStatus.borderColor} text-[11px] text-slate-300`}>
                        Inhaber-Gesamtertrag (vor ESt):{' '}
                        <strong className="font-mono text-[#dcb68a]">
                          {formatCurrency(outputs.vergleich.inhaberErtragMonat.nachher)} / Monat
                        </strong>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* 5. RESULTIERENDE STUNDENSÄTZE (60 MIN. BRUTTO) */}
              <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <span className="text-xs font-black uppercase tracking-wider text-white">
                    Stundensätze (60 Min. · Brutto) — Vorher (Kalkulator) vs. Szenario
                  </span>
                  <span className="text-xs font-mono text-slate-400">
                    {outputs.isEffectiveSolo ? 'Unternehmerstundenlohn' : 'Ø Team-Stundenlohn'}:{' '}
                    {formatCurrency(
                      outputs.isEffectiveSolo
                        ? outputs.neuerInhaberProdStundenlohn
                        : outputs.neuerTeamStundenlohn,
                      2
                    )}{' '}
                    / Std.
                    {outputs.isEffectiveSolo ? ' · ohne Lohnnebenkosten' : ''}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div
                    className="rounded-xl bg-slate-900 border border-[#dcb68a]/40 p-4 flex items-center justify-between gap-4"
                    data-pe-tooltip="Vergleicht deinen aktuell im Kalkulator hinterlegten Stundensatz ohne Material (Vorher) mit dem neuen Stundensatz, der sich durch deine geplanten Änderungen im Szenario ergibt."
                  >
                    <div>
                      <span className="text-xs font-black uppercase tracking-wider text-[#dcb68a] block">
                        Stundensatz ohne Material
                      </span>
                      <span className="text-xs text-slate-400 font-mono mt-1 block">
                        Vorher im Kalkulator:{' '}
                        <strong className="text-slate-200">
                          {formatCurrency(outputs.stundensaetze.ohneMaterialBreakEvenBrutto, 2)}
                        </strong>
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                        Neu im Szenario
                      </span>
                      <span className="text-xl font-mono font-black text-[#dcb68a]">
                        {formatCurrency(outputs.stundensaetze.ohneMaterialMitRuecklageBrutto, 2)}
                      </span>
                    </div>
                  </div>

                  <div
                    className="rounded-xl bg-slate-900 border border-[#dcb68a]/40 p-4 flex items-center justify-between gap-4"
                    data-pe-tooltip="Vergleicht deinen im Kalkulator hinterlegten Stundensatz inklusive 50g Material (Vorher) mit dem neuen Ziel-Stundensatz im Szenario."
                  >
                    <div>
                      <span className="text-xs font-black uppercase tracking-wider text-[#dcb68a] block">
                        Stundensatz inkl. {globalSettings.standardMaterialGrammProStunde}g Material
                      </span>
                      <span className="text-xs text-slate-400 font-mono mt-1 block">
                        Vorher im Kalkulator:{' '}
                        <strong className="text-slate-200">
                          {formatCurrency(outputs.stundensaetze.inklMaterialBreakEvenBrutto, 2)}
                        </strong>
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                        Neu im Szenario
                      </span>
                      <span className="text-xl font-mono font-black text-white">
                        {formatCurrency(outputs.stundensaetze.inklMaterialMitRuecklageBrutto, 2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 6. VORHER / NACHHER VERGLEICHSTABELLE (MIT PROZENTWERTEN HINTEN IN DEN SPALTEN) */}
              <div className="overflow-x-auto rounded-xl border border-slate-800">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-slate-800 bg-slate-950/90 text-[11px] font-black uppercase tracking-wider text-slate-400">
                      <th className="px-4 py-3">Position (Monat)</th>
                      <th className="px-4 py-3 text-right">Ist (Baseline)</th>
                      <th className="px-4 py-3 text-right text-[#dcb68a]">Szenario (Neu)</th>
                      <th className="px-4 py-3 text-right">Abweichung</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ReportCompareRow
                      label="Monatsumsatz (Brutto)"
                      subtitle={`Netto: ${formatCurrency(outputs.vergleich.monatsumsatzNetto.vorher)} → ${formatCurrency(outputs.vergleich.monatsumsatzNetto.nachher)}`}
                      comp={outputs.vergleich.monatsumsatzBrutto}
                      formatVal={(v) => formatCurrency(v)}
                    />
                    <ReportCompareRow
                      label="Materialeinsatz / Wareneinsatz (Netto)"
                      comp={outputs.vergleich.materialkostenMonatNetto}
                      quoteComp={outputs.vergleich.materialkostenfaktorPercent}
                      formatVal={(v) => formatCurrency(v)}
                      invertGood={true}
                    />
                    <ReportCompareRow
                      label="Produktive Personalkosten (inkl. Chef)"
                      comp={outputs.vergleich.produktivePersonalkostenMonat}
                      quoteComp={outputs.vergleich.prodLohnkostenfaktorPercent}
                      formatVal={(v) => formatCurrency(v)}
                      invertGood={true}
                    />
                    <ReportCompareRow
                      label="Gemeinkosten / Fixkosten (Netto)"
                      comp={outputs.vergleich.gemeinkostenMonatNetto}
                      quoteComp={outputs.vergleich.gemeinkostenfaktorPercent}
                      formatVal={(v) => formatCurrency(v)}
                      invertGood={true}
                    />
                    <ReportCompareRow
                      label="Gesamtkosten / Sollumsatz (Break-Even Netto)"
                      comp={outputs.vergleich.gesamtkostenSollumsatzNetto}
                      formatVal={(v) => formatCurrency(v)}
                      invertGood={true}
                    />
                    <ReportCompareRow
                      label="Kalkulatorischer Überschuss / Verlust"
                      comp={outputs.vergleich.ueberschussMonatNetto}
                      quoteComp={outputs.vergleich.ueberschussPercent}
                      formatVal={(v) => formatCurrency(v)}
                      isResult={true}
                    />
                    <ReportCompareRow
                      label="Inhaber-Gesamtertrag (mtl.)"
                      comp={outputs.vergleich.inhaberErtragMonat}
                      quoteComp={outputs.vergleich.rentabilitaetPercent}
                      formatVal={(v) => formatCurrency(v)}
                      isResult={true}
                    />
                    <ReportCompareRow
                      label="Kalkulatorische Rentabilität"
                      comp={outputs.vergleich.rentabilitaetPercent}
                      formatVal={(v) => fmtPct(v, 1)}
                      isResult={true}
                    />
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        </div>

        {/* ====================================================================
            4A. MITARBEITER-SOLLUMSATZ & STUNDENSATZ-MACHBARKEIT (AUF-/ZUKLAPPBAR)
            ==================================================================== */}
        {(() => {
          const mwstFaktor = 1 + (globalSettings.mwstSatzPercent || 19) / 100;
          const istSalonStundensatzBrutto =
            baseline.barometerStundenfaktor > 0
              ? baseline.barometerStundenfaktor
              : outputs.stundensaetze.ohneMaterialBreakEvenBrutto;
          const geplantePreisPct = inputs.preisanpassung.geplantePreiserhoehungPercent || 0;
          const aktuellerSzenarioStundensatzBrutto =
            istSalonStundensatzBrutto * Math.max(0, 1 + geplantePreisPct / 100);

          const activeRows = outputs.mitarbeiterSollUmsaetze.filter((r) => !r.isDeparted);
          const unreachableCount = activeRows.filter(
            (r) => r.requiredStundensatzBrutto > aktuellerSzenarioStundensatzBrutto + 0.5
          ).length;

          return (
            <section className="bg-slate-900/95 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
              <button
                type="button"
                onClick={() => setMitarbeiterSpiegelOpen((prev) => !prev)}
                className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left hover:bg-slate-950/40 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Users size={20} className="text-[#dcb68a] shrink-0" />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-base font-black uppercase tracking-wider text-white">
                        Mitarbeiter-Sollumsatz &amp; Stundensatz-Machbarkeit
                      </h2>
                      {unreachableCount > 0 ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-500/20 text-rose-300 border border-rose-500/40">
                          {unreachableCount}× mit aktuellem Preis nicht erreichbar
                        </span>
                      ) : activeRows.length > 0 ? (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                          Alle Stundensätze erreichbar
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Prüft pro Mitarbeiter, welcher Stundensatz bei {fmtPct(globalSettings.auslastungsfaktorPercent, 0)} Auslastung nötig ist — und ob dein aktueller Salonpreis ({formatCurrency(aktuellerSzenarioStundensatzBrutto, 2)}/Std.) dafür ausreicht.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#dcb68a] shrink-0">
                  <span>{mitarbeiterSpiegelOpen ? 'Zuklappen' : 'Aufklappen'}</span>
                  {mitarbeiterSpiegelOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
              </button>

              {mitarbeiterSpiegelOpen && (
                <div className="p-5 md:p-6 pt-0 border-t border-slate-800 space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 pt-4 text-xs text-slate-400">
                    <div>
                      Aktueller Salon-Stundensatz (Ist):{' '}
                      <strong className="font-mono text-white">
                        {formatCurrency(istSalonStundensatzBrutto, 2)} / Std.
                      </strong>
                      {Math.abs(geplantePreisPct) >= 0.1 && (
                        <>
                          {' '}· Mit deiner geplanten Preisanpassung ({geplantePreisPct > 0 ? '+' : ''}
                          {fmtPct(geplantePreisPct, 1)}):{' '}
                          <strong className="font-mono text-[#dcb68a]">
                            {formatCurrency(aktuellerSzenarioStundensatzBrutto, 2)} / Std.
                          </strong>
                        </>
                      )}
                    </div>
                    <div className="font-mono">
                      {outputs.isEffectiveSolo ? (
                        <>
                          Solo · ohne Lohnfaktor/KDF · Ziel-Auslastung:{' '}
                          <strong className="text-white">{fmtPct(globalSettings.auslastungsfaktorPercent, 0)}</strong>
                        </>
                      ) : (
                        <>
                          Lohnfaktor Aktiv: <strong className="text-[#dcb68a]">{fmtNum(outputs.neuerLohnfaktorAktiv, 2)}</strong> · KDF: <strong className="text-white">{fmtNum(outputs.neuerKostendeckungsfaktor, 2)}</strong> · Ziel-Auslastung: <strong className="text-white">{fmtPct(globalSettings.auslastungsfaktorPercent, 0)}</strong>
                        </>
                      )}
                    </div>
                  </div>

                  {outputs.mitarbeiterSollUmsaetze.length === 0 ? (
                    <div className="text-xs text-slate-500 py-4 text-center">
                      Keine produktiven Mitarbeiter im Bestand oder Zuwachs.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {outputs.mitarbeiterSollUmsaetze.map((row) => {
                        const auslastungQuote = (globalSettings.auslastungsfaktorPercent || 75) / 100;
                        const produktiveStundenMonat = row.monatsstunden * auslastungQuote;
                        const istStundensatzVorher =
                          produktiveStundenMonat > 0
                            ? row.sollUmsatzVorherBrutto / produktiveStundenMonat
                            : 0;
                        const sollStundensatzNeu = row.requiredStundensatzBrutto;

                        // Vergleich mit dem aktuellen Salon-Stundensatz (inkl. evtl. eingestellter Preiserhöhung)
                        const refRate =
                          aktuellerSzenarioStundensatzBrutto > 0
                            ? aktuellerSzenarioStundensatzBrutto
                            : istStundensatzVorher;
                        const isUnreachable =
                          !row.isDeparted && refRate > 0 && sollStundensatzNeu > refRate + 0.5;
                        const neededFactor =
                          refRate > 0 ? sollStundensatzNeu / refRate : 1;
                        const neededIncreasePct = (neededFactor - 1) * 100;

                        return (
                          <div
                            key={row.id}
                            className={`rounded-xl border p-4 space-y-3 transition-colors ${
                              row.isDeparted
                                ? 'opacity-45 bg-rose-950/10 border-rose-900/40'
                                : isUnreachable
                                  ? 'bg-slate-950/90 border-rose-500/50'
                                  : 'bg-slate-950/90 border-emerald-500/40'
                            }`}
                          >
                            {/* Kopfzeile Mitarbeiter */}
                            <div className="flex items-center justify-between gap-2 border-b border-slate-800 pb-2.5">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-sm font-black text-white truncate">
                                  {row.name}
                                </span>
                                {row.isNewHire && (
                                  <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                    Neu
                                  </span>
                                )}
                                {row.isDeparted && (
                                  <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-rose-500/20 text-rose-300">
                                    Abgang
                                  </span>
                                )}
                              </div>
                              <span className="text-xs font-mono text-slate-400 shrink-0">
                                {fmtNum(row.wochenstunden, 1)} Std./Wo. · Brutto {formatCurrency(row.bruttoGehaltMonat)}
                              </span>
                            </div>

                            {!row.isDeparted && (
                              <>
                                {/* Monats-Umsatz-Spiegel */}
                                <div className="grid grid-cols-3 gap-2 text-xs bg-slate-900/70 rounded-lg p-2.5 border border-slate-800/80">
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wider text-slate-400 block">
                                      Mindestumsatz (KDF)
                                    </span>
                                    <span className="font-mono font-bold text-slate-200">
                                      {formatCurrency(row.kostendeckungUmsatzNeuBrutto)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-[10px] uppercase tracking-wider text-slate-400 block">
                                      Soll Vorher
                                    </span>
                                    <span className="font-mono font-semibold text-slate-300">
                                      {formatCurrency(row.sollUmsatzVorherBrutto)}
                                    </span>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-[10px] uppercase tracking-wider text-[#dcb68a] font-bold block">
                                      Soll Neu (LF {fmtNum(outputs.neuerLohnfaktorAktiv, 2)})
                                    </span>
                                    <span className="font-mono font-black text-[#dcb68a]">
                                      {formatCurrency(row.sollUmsatzNeuBrutto)}
                                    </span>
                                  </div>
                                </div>

                                {/* Stundensatz Ist vs. Benötigt (Soll) */}
                                <div className="grid grid-cols-2 gap-3 pt-1">
                                  <div className="rounded-lg bg-slate-900 border border-slate-800 p-2.5">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                                      Aktueller Salon-Stundensatz (Ist)
                                    </span>
                                    <span className="text-base font-mono font-bold text-slate-200 mt-0.5 block">
                                      {formatCurrency(refRate, 2)} / Std.
                                    </span>
                                    <span className="text-[10px] font-mono text-slate-500">
                                      ({formatCurrency(refRate / mwstFaktor, 2)} Netto)
                                    </span>
                                  </div>

                                  <div
                                    className={`rounded-lg p-2.5 border ${
                                      isUnreachable
                                        ? 'bg-rose-950/25 border-rose-500/50'
                                        : 'bg-emerald-950/20 border-emerald-500/40'
                                    }`}
                                  >
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300 block">
                                      Benötigter Umsatz / Std. (Soll)
                                    </span>
                                    <span
                                      className={`text-base font-mono font-black mt-0.5 block ${
                                        isUnreachable ? 'text-rose-400' : 'text-emerald-400'
                                      }`}
                                    >
                                      {formatCurrency(sollStundensatzNeu, 2)} / Std.
                                    </span>
                                    <span className="text-[10px] font-mono text-slate-400">
                                      bei {fmtPct(globalSettings.auslastungsfaktorPercent, 0)} Auslastung
                                    </span>
                                  </div>
                                </div>

                                {/* Klartext-Diagnose: Erreichbar oder nicht erreichbar mit aktuellen Preisen */}
                                <div
                                  className={`rounded-lg px-3 py-2 text-xs font-medium leading-snug border ${
                                    isUnreachable
                                      ? 'bg-rose-950/30 border-rose-500/40 text-rose-300'
                                      : 'bg-emerald-950/25 border-emerald-500/30 text-emerald-300'
                                  }`}
                                >
                                  {isUnreachable ? (
                                    <>
                                      <strong className="font-black uppercase tracking-wide block mb-0.5">
                                        ⚠️ Mit den aktuellen Preisen nicht erreichbar!
                                      </strong>
                                      Um diesen Sollumsatz in {fmtNum(row.wochenstunden, 1)} Std./Wo. zu schaffen, müsste der Preis für diesen Mitarbeiter mindestens um{' '}
                                      <strong className="font-mono text-white underline">
                                        +{fmtPct(neededIncreasePct, 1)}
                                      </strong>{' '}
                                      (Faktor <strong className="font-mono text-white">{fmtNum(neededFactor, 2)}×</strong>) auf{' '}
                                      <strong className="font-mono text-white">
                                        {formatCurrency(sollStundensatzNeu, 2)}/Std.
                                      </strong>{' '}
                                      steigen.
                                    </>
                                  ) : (
                                    <>
                                      <strong className="font-black uppercase tracking-wide">
                                        ✔ Mit aktuellen Preisen erreichbar:
                                      </strong>{' '}
                                      Der aktuelle Stundensatz ({formatCurrency(refRate, 2)}/Std.) reicht aus, um das Umsatzziel ({formatCurrency(sollStundensatzNeu, 2)}/Std.) bei {fmtPct(globalSettings.auslastungsfaktorPercent, 0)} Auslastung zu erwirtschaften.
                                    </>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })()}

        {/* ====================================================================
            4B. DIENSTLEISTUNGS-KALKULATOR (AUFKLAPPBARES MODUL)
            ==================================================================== */}
        {serviceKalkResult && (
          <section className="bg-slate-900/95 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
            <button
              type="button"
              onClick={() => setKalkulatorOpen((prev) => !prev)}
              className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left hover:bg-slate-950/40 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Calculator size={20} className="text-[#dcb68a] shrink-0" />
                <div>
                  <h2 className="text-base font-black uppercase tracking-wider text-white">
                    Dienstleistungs-Kalkulator (Szenario-Werte)
                  </h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Einzelne Dienstleistung mit den neuen Quoten gegenprüfen (Empfehlung inkl. Rücklage:{' '}
                    <strong className="text-[#dcb68a]">
                      {formatCurrency(serviceKalkResult.empfohlenerZielPreisMitRuecklageBrutto, 2)}
                    </strong>
                    )
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#dcb68a] shrink-0">
                <span>{kalkulatorOpen ? 'Zuklappen' : 'Aufklappen'}</span>
                {kalkulatorOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </div>
            </button>

            {kalkulatorOpen && (
              <div className="p-5 md:p-6 pt-0 border-t border-slate-800 space-y-5">
                {kalkulator.isManualNeuerPreis && (
                  <div className="flex justify-end pt-3">
                    <button
                      type="button"
                      onClick={resetKalkulatorNeuerPreisToAuto}
                      className="px-3 py-1.5 rounded-lg border border-[#dcb68a]/40 bg-[#dcb68a]/10 text-[10px] font-bold uppercase tracking-wider text-[#dcb68a] hover:bg-[#dcb68a]/20"
                    >
                      Zielpreis wieder automatisch setzen
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 pt-3 items-start">
                  {/* LINKS: 9 VON 12 SPALTEN FÜR DEN KALKULATOR */}
                  <div className="xl:col-span-9 min-w-0 w-full space-y-5">
                    {/* 1:1 EINE LEISTE: bisher. Preis | Bedienzeit | neuer Preis | Anpassung */}
                    {(() => {
                      const mins = kalkulator.bedienzeitMinuten || 60;
                      const vor60 = outputs.stundensaetze.ohneMaterialBreakEvenBrutto;
                      const vorBeiZeit = Number(((vor60 / 60) * mins).toFixed(2));
                      const anp = serviceKalkResult.currentAnpassungPercent;

                      return (
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-start bg-slate-950/90 border border-slate-800 rounded-xl p-4">
                          <div className="flex flex-col min-w-0 space-y-1">
                            <CompactCalcField
                              label="bisher. Preis"
                              value={kalkulator.bisherigerPreisBrutto}
                              onChange={(v) =>
                                updateKalkulatorInputs({ bisherigerPreisBrutto: v })
                              }
                              min={0}
                              max={1000}
                              step={5}
                              decimals={2}
                              unit={currencyCode}
                            />
                            <div className="text-[11px] font-mono text-slate-400 text-center h-4 leading-4 truncate">
                              Vorher ({mins} Min.): {formatCurrency(vorBeiZeit, 2)}
                            </div>
                          </div>

                          <div className="flex flex-col min-w-0 space-y-1">
                            <CompactCalcField
                              label="Bedienzeit"
                              value={kalkulator.bedienzeitMinuten}
                              onChange={(v) => {
                                const nextMins = Math.max(5, v);
                                const nextBisher = Number(((vor60 / 60) * nextMins).toFixed(2));
                                updateKalkulatorInputs({
                                  bedienzeitMinuten: nextMins,
                                  bisherigerPreisBrutto:
                                    vor60 > 0 ? nextBisher : kalkulator.bisherigerPreisBrutto,
                                  isManualNeuerPreis: false,
                                });
                              }}
                              min={5}
                              max={360}
                              step={5}
                              decimals={0}
                              unit="min."
                            />
                            <div className="text-[11px] font-mono text-slate-400 text-center h-4 leading-4 truncate">
                              Basis (60 Min.): {formatCurrency(vor60, 2)}
                            </div>
                          </div>

                          <div className="flex flex-col min-w-0 space-y-1">
                            <CompactCalcField
                              label="neuer Preis"
                              value={kalkulator.neuerPreisBrutto}
                              onChange={(v) =>
                                updateKalkulatorInputs({
                                  neuerPreisBrutto: v,
                                  isManualNeuerPreis: true,
                                })
                              }
                              min={0}
                              max={1500}
                              step={5}
                              decimals={2}
                              unit={currencyCode}
                            />
                            <div className="text-[11px] font-mono text-[#dcb68a] font-bold text-center h-4 leading-4 truncate">
                              Ziel: {formatCurrency(serviceKalkResult.empfohlenerZielPreisMitRuecklageBrutto, 2)}
                            </div>
                          </div>

                          <div className="flex flex-col min-w-0 items-center space-y-1">
                            <span className="text-[11px] font-black uppercase tracking-wider text-slate-300 mb-1.5 h-[1.125rem] leading-[1.125rem]">
                              Anpassung
                            </span>
                            <div className="peplan-step-wrap w-full">
                              <button
                                type="button"
                                className="peplan-step-btn"
                                aria-label="Anpassung verringern"
                                onClick={() => {
                                  const b = kalkulator.bisherigerPreisBrutto;
                                  if (!(b > 0)) return;
                                  const rem = ((anp % 1) + 1) % 1;
                                  const isAligned = rem < 0.05 || 1 - rem < 0.05;
                                  const targetPct = !isAligned
                                    ? Math.floor(anp)
                                    : Math.round(anp) - 1;
                                  const nextPrice = Math.max(
                                    0,
                                    Number((b * (1 + targetPct / 100)).toFixed(2))
                                  );
                                  updateKalkulatorInputs({
                                    neuerPreisBrutto: nextPrice,
                                    isManualNeuerPreis: true,
                                  });
                                }}
                              >
                                −
                              </button>
                              <div className="flex-1 min-w-0 flex items-center justify-center rounded-lg bg-slate-900 border border-slate-800 px-2 h-[2.25rem] text-center">
                                <span
                                  className={`text-sm font-mono font-black leading-none whitespace-nowrap ${
                                    anp >= 25
                                      ? 'text-rose-400'
                                      : anp > 15
                                        ? 'text-amber-400'
                                        : 'text-emerald-400'
                                  }`}
                                >
                                  {anp > 0 ? '+' : ''}
                                  {fmtPct(anp, 1)}
                                </span>
                              </div>
                              <button
                                type="button"
                                className="peplan-step-btn"
                                aria-label="Anpassung erhöhen"
                                onClick={() => {
                                  const b = kalkulator.bisherigerPreisBrutto;
                                  if (!(b > 0)) return;
                                  const rem = ((anp % 1) + 1) % 1;
                                  const isAligned = rem < 0.05 || 1 - rem < 0.05;
                                  const targetPct = !isAligned
                                    ? Math.ceil(anp)
                                    : Math.round(anp) + 1;
                                  const nextPrice = Math.max(
                                    0,
                                    Number((b * (1 + targetPct / 100)).toFixed(2))
                                  );
                                  updateKalkulatorInputs({
                                    neuerPreisBrutto: nextPrice,
                                    isManualNeuerPreis: true,
                                  });
                                }}
                              >
                                +
                              </button>
                            </div>
                            <div
                              className={`text-[10px] font-bold text-center h-4 leading-4 truncate w-full ${
                                anp >= 25
                                  ? 'text-rose-400'
                                  : anp > 15
                                    ? 'text-amber-400'
                                    : 'text-emerald-400'
                              }`}
                            >
                              {anp >= 25
                                ? 'Anpassung viel zu hoch! 🚨'
                                : anp > 15
                                  ? 'Anpassung ist in Ordnung. ↗️'
                                  : 'Anpassung ist gut. ↗️'}
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Kaskade links + 3 schmale Boxen (Mat. EUR / Mat. g / Ø Stundenlohn) rechts wie im Original-Kalkulator */}
                    <div className="flex flex-col lg:flex-row items-stretch gap-4">
                      {/* Kaskaden-Tabelle */}
                      <div className="flex-grow rounded-xl bg-slate-950/90 border border-slate-800 p-4 space-y-2 text-xs">
                        <div className="grid grid-cols-4 font-bold text-slate-300 pb-2 border-b border-slate-800">
                          <span className="col-span-1">Position</span>
                          <span className="text-center">Quote</span>
                          <span className="text-right">Abzug</span>
                          <span className="text-right">Netto</span>
                        </div>

                        <div className="grid grid-cols-4 py-1 text-slate-300">
                          <span>abzügl. MwSt.</span>
                          <span className="text-center font-mono text-slate-400">
                            {fmtPct(globalSettings.mwstSatzPercent, 1)}
                          </span>
                          <span className="text-right font-mono text-[#dcb68a]">
                            {formatCurrency(serviceKalkResult.mwstValue, 2)}
                          </span>
                          <span className="text-right font-mono font-bold text-[#dcb68a]">
                            {formatCurrency(serviceKalkResult.nettoNachMwst, 2)}
                          </span>
                        </div>

                        {serviceKalkResult.totalActivatedDevicePricePercent > 0 && (
                          <div className="grid grid-cols-4 py-1 text-slate-300">
                            <span>abzügl. Geräte</span>
                            <span className="text-center font-mono text-slate-400">
                              {fmtPct(serviceKalkResult.totalActivatedDevicePricePercent, 1)}
                            </span>
                            <span className="text-right font-mono text-[#dcb68a]">
                              {formatCurrency(serviceKalkResult.totalActivatedDevicePrice, 2)}
                            </span>
                            <span />
                          </div>
                        )}

                        <div className="grid grid-cols-4 py-1 text-slate-300">
                          <span>abzügl. Material</span>
                          <span className="text-center font-mono text-slate-400">
                            {fmtPct(serviceKalkResult.totalMaterialCostPercent, 1)}
                          </span>
                          <span className="text-right font-mono text-[#dcb68a]">
                            {formatCurrency(serviceKalkResult.totalMaterialCost, 2)}
                          </span>
                          <span />
                        </div>

                        <div className="grid grid-cols-4 py-1 text-slate-300">
                          <span>abzügl. Gemeinkosten</span>
                          <span className="text-center font-mono text-slate-400">
                            {fmtPct(outputs.neuerGemeinkostenfaktor * 100, 1)}
                          </span>
                          <span className="text-right font-mono text-[#dcb68a]">
                            {formatCurrency(serviceKalkResult.gemeinkostenValue, 2)}
                          </span>
                          <span />
                        </div>

                        {!outputs.isEffectiveSolo && (
                        <div className="grid grid-cols-4 py-1 text-slate-300">
                          <span>abzügl. unpr. Unternehmerlohn</span>
                          <span className="text-center font-mono text-slate-400">
                            {fmtPct(outputs.neuerUnprodUnternehmerlohnPercent, 1)}
                          </span>
                          <span className="text-right font-mono text-[#dcb68a]">
                            {formatCurrency(serviceKalkResult.unternehmerlohnValue, 2)}
                          </span>
                          <span />
                        </div>
                        )}

                        <div className="grid grid-cols-4 py-1 text-slate-300">
                          <span>abzügl. kalk. Verlust</span>
                          <span className="text-center font-mono text-slate-400">
                            {fmtPct(globalSettings.verlustPercent, 1)}
                          </span>
                          <span className="text-right font-mono text-[#dcb68a]">
                            {formatCurrency(serviceKalkResult.verlustValue, 2)}
                          </span>
                          <span />
                        </div>

                        <div className="grid grid-cols-2 py-2 border-t border-slate-800 font-bold text-white text-sm">
                          <span>übrig für Lohnkosten</span>
                          <span className="text-right font-mono text-[#dcb68a]">
                            {formatCurrency(serviceKalkResult.uebrigFuerLohn, 2)}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 py-2.5 px-3 rounded-lg bg-slate-900 border border-slate-800 font-bold text-slate-200">
                          <span>
                            {outputs.isEffectiveSolo
                              ? 'abzügl. Unternehmerlohn (ohne Lohnnebenkosten)'
                              : 'abzügl. Lohn & Nebenkosten'}
                          </span>
                          <span className="text-right font-mono text-[#dcb68a] text-base">
                            {formatCurrency(serviceKalkResult.lohnkostenValue, 2)}
                          </span>
                        </div>
                      </div>

                      {/* 3 Seiten-Boxen rechts neben der Kaskade (kompakt ohne Überlappung) */}
                      <div className="w-full lg:w-48 shrink-0 flex flex-col justify-between gap-3">
                        <div className="rounded-xl bg-slate-950/90 border border-slate-800 p-3">
                          <CompactCalcField
                            label={`Mat. ${currencyCode}`}
                            value={kalkulator.materialPreisNetto}
                            onChange={(v) => updateKalkulatorInputs({ materialPreisNetto: v })}
                            min={0}
                            max={500}
                            step={5}
                            decimals={2}
                            unit={currencyCode}
                          />
                        </div>
                        <div className="rounded-xl bg-slate-950/90 border border-[#dcb68a]/40 p-3">
                          <CompactCalcField
                            label="Mat. g"
                            value={kalkulator.materialGramm}
                            onChange={(v) => updateKalkulatorInputs({ materialGramm: v })}
                            min={0}
                            max={500}
                            step={5}
                            decimals={0}
                            unit="g"
                            accent={true}
                          />
                        </div>
                        <div className="rounded-xl bg-slate-950/90 border border-slate-800 p-3 text-center">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">
                            {outputs.isEffectiveSolo ? 'Unternehmerlohn / Std.' : 'Ø Team-Stundenlohn'}
                          </span>
                          <span className="text-base font-mono font-black text-[#dcb68a]">
                            {formatCurrency(
                              outputs.isEffectiveSolo
                                ? outputs.neuerInhaberProdStundenlohn
                                : outputs.neuerTeamStundenlohn,
                              2
                            )}
                          </span>
                          {outputs.isEffectiveSolo && (
                            <span className="text-[10px] text-slate-500 block mt-1">ohne Lohnnebenkosten</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <PePlanGewinnGauge
                      gewinn={serviceKalkResult.gewinn}
                      gewinnPercent={serviceKalkResult.gewinnPercent}
                      targetPercent={globalSettings.ruecklagenSatzPercent}
                      formatCurrency={formatCurrency}
                    />
                  </div>

                  {/* RECHTS: 3 VON 12 SPALTEN FÜR MATERIAL- & GERÄTE-AKTIVIERUNG */}
                  <div className="xl:col-span-3 min-w-0 w-full bg-slate-950/80 border border-slate-800 rounded-xl p-4 flex flex-col justify-between space-y-5">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                        <h3 className="text-xs font-black uppercase tracking-wider text-[#dcb68a]">
                          Material aktivieren (aus Kalkulator)
                        </h3>
                        <span className="text-[10px] font-mono text-slate-500">EK / 100g</span>
                      </div>

                      {baseline.materialListe.length === 0 ? (
                        <p className="text-xs text-slate-500">
                          Kein Materialstamm in der Akte hinterlegt.
                        </p>
                      ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                          {baseline.materialListe.map((m) => {
                            const per100 = m.gebinde > 0 ? (m.ekPreis / m.gebinde) * 100 : 0;
                            return (
                              <label
                                key={m.id}
                                className="flex items-center justify-between gap-2 text-xs cursor-pointer py-1 px-2 rounded hover:bg-slate-900"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <input
                                    type="checkbox"
                                    checked={activeMatSet.has(m.id)}
                                    onChange={() => toggleKalkulatorMaterial(m.id)}
                                    className="h-3.5 w-3.5 accent-[#dcb68a]"
                                  />
                                  <span className="text-slate-300 truncate">{m.name}</span>
                                </div>
                                <span className="font-mono text-[#dcb68a] shrink-0">
                                  {formatCurrency(per100, 2)}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {baseline.geraeteListe.length > 0 && (
                      <div className="space-y-3 pt-4 border-t border-slate-800">
                        <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                          <h3 className="text-xs font-black uppercase tracking-wider text-[#dcb68a]">
                            Geräte aktivieren
                          </h3>
                          <span className="text-[10px] font-mono text-slate-500">Kosten-%</span>
                        </div>
                        <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                          {baseline.geraeteListe.map((d) => (
                            <label
                              key={d.id}
                              className="flex items-center justify-between gap-2 text-xs cursor-pointer py-1 px-2 rounded hover:bg-slate-900"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <input
                                  type="checkbox"
                                  checked={activeDevSet.has(d.id)}
                                  onChange={() => toggleKalkulatorDevice(d.id)}
                                  className="h-3.5 w-3.5 accent-[#dcb68a]"
                                />
                                <span className="text-slate-300 truncate">{d.name}</span>
                              </div>
                              <span className="font-mono text-[#dcb68a] shrink-0">
                                {fmtPct(d.kostenProzent, 1)}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="rounded-lg bg-slate-900/90 border border-slate-800 p-3 flex items-start gap-2.5 text-[11px] text-slate-400">
                      <AlertTriangle size={15} className="text-[#dcb68a] shrink-0 mt-0.5" />
                      <span>
                        Alle Änderungen sind zu 100 % in der Sandbox gekapselt und verändern keine
                        Live-Daten der Mandantenakte.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
};

export default PePlanPage;
